import { supabase, supabaseAuth, getRefreshTokenCookie, deleteRefreshTokenCookie } from './supabase';
import { getLoyaltyStatus, afterCheckIn } from './loyalty';
import * as notify from './notify';
import { calculateRatingPoints, updatePlayerRating, calculateXP, calculateLevel, TournamentResult } from './ratingCalculator';

export async function adminLogin(login: string, password: string): Promise<{ token: string; role: 'owner' | 'admin' }> {
  const ownerLogin  = import.meta.env.VITE_OWNER_LOGIN;
  const ownerSecret = import.meta.env.VITE_OWNER_SECRET;
  const adminLogin  = import.meta.env.VITE_ADMIN_LOGIN;
  const adminSecret = import.meta.env.VITE_ADMIN_SECRET;

  if (ownerLogin && ownerSecret && login === ownerLogin && password === ownerSecret) {
    const token = `owner:${ownerSecret}`;
    return { token, role: 'owner' };
  }
  if (adminLogin && adminSecret && login === adminLogin && password === adminSecret) {
    const token = `admin:${adminSecret}`;
    return { token, role: 'admin' };
  }
  throw new Error('Неверный логин или пароль');
}

export async function verifyAdminToken(token: string): Promise<{ ok: boolean; role?: 'owner' | 'admin' }> {
  const [role, ...rest] = token.split(':');
  const secret = rest.join(':');
  const ownerSecret = import.meta.env.VITE_OWNER_SECRET;
  const adminSecret = import.meta.env.VITE_ADMIN_SECRET;

  if (role === 'owner' && ownerSecret && secret === ownerSecret) return { ok: true, role: 'owner' };
  if (role === 'admin' && adminSecret && secret === adminSecret) return { ok: true, role: 'admin' };
  return { ok: false };
}

/** Текущее московское время (GMT+3) в формате ISO без смещения */
export function moscowNow(): string {
  return new Date().toLocaleString('sv-SE', { timeZone: 'Europe/Moscow' }).replace(' ', 'T');
}

function safeParseJSON(value: any, fallback: any = []): any {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return fallback; }
}

/* ─── Referral Helpers ─── */

/** Link referral from localStorage after new user creation */
async function _linkReferral(newUserId: number) {
  try {
    const refCode = localStorage.getItem('referral_code');
    if (!refCode) return;
    localStorage.removeItem('referral_code');
    const referrerId = parseInt(refCode, 10);
    if (isNaN(referrerId) || referrerId === newUserId) return;
    const { data: referrer } = await supabase.from('users').select('id').eq('id', referrerId).maybeSingle();
    if (!referrer) return;
    await supabase.from('referrals').insert({ referrer_id: referrerId, referred_id: newUserId });
  } catch { /* ignore duplicates */ }
}

/** Process referral reward on first check-in */
export async function processReferralReward(userId: number) {
  // Mark as 'processing' first — prevents double-award if undo+redo check-in races
  const { data: updated } = await supabase
    .from('referrals')
    .update({ status: 'processing' })
    .eq('referred_id', userId).eq('status', 'pending')
    .select('id, referrer_id')
    .maybeSingle();
  if (!updated) return; // already processed or no referral

  // Award referrer: +1 free entry, +50 coins
  const { data: rr } = await supabase
    .from('player_ratings').select('coins_balance, free_entries')
    .eq('user_id', updated.referrer_id).single();
  if (rr) {
    await supabase.from('player_ratings').update({
      coins_balance: (rr.coins_balance || 0) + 50,
      free_entries: (rr.free_entries || 0) + 1,
    }).eq('user_id', updated.referrer_id);
  }

  // Award referred: +50 coins
  const { data: ur } = await supabase
    .from('player_ratings').select('coins_balance')
    .eq('user_id', userId).single();
  if (ur) {
    await supabase.from('player_ratings').update({
      coins_balance: (ur.coins_balance || 0) + 50,
    }).eq('user_id', userId);
  }

  // Mark completed
  await supabase.from('referrals').update({
    status: 'completed', completed_at: moscowNow(),
  }).eq('id', updated.id);
}

/** Get count of completed referrals for a user */
export async function getReferralCount(userId: number): Promise<number> {
  const { count } = await supabase
    .from('referrals').select('*', { count: 'exact', head: true })
    .eq('referrer_id', userId).eq('status', 'completed');
  return count || 0;
}

/* ═══════════════════════════════════════════════════════
   EMAIL AUTH (Supabase Auth OTP)
   ═══════════════════════════════════════════════════════ */

export async function sendEmailOtp(email: string) {
  const { error } = await supabaseAuth.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: true },
  });
  if (error) throw error;
}

export async function verifyEmailOtp(email: string, token: string) {
  const { data, error } = await supabaseAuth.auth.verifyOtp({
    email,
    token,
    type: 'email',
  });
  if (error) throw error;
  return data;
}

/* ═══════════════════════════════════════════════════════
   NO-SHOW DETECTION
   ═══════════════════════════════════════════════════════ */

export async function checkNoShows(userId: number): Promise<{ hasNoShow: boolean; count: number }> {
  // Find tournaments that started >4h ago where user was registered but didn't check in
  const fourHoursAgo = new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString();

  const { data: regs } = await supabase
    .from('tournament_registrations')
    .select('*, tournaments!inner(start_date)')
    .eq('user_id', userId)
    .eq('status', 'registered')
    .eq('checked_in', false)
    .lt('tournaments.start_date', fourHoursAgo);

  if (!regs || regs.length === 0) {
    return { hasNoShow: false, count: 0 };
  }

  // Mark these as no-shows (cancel them so they don't trigger again)
  for (const reg of regs) {
    await supabase.from('tournament_registrations')
      .update({ status: 'cancelled' })
      .eq('id', reg.id);
  }

  return { hasNoShow: true, count: regs.length };
}

export async function getAuthSession() {
  const { data: { session } } = await supabaseAuth.auth.getSession();
  if (session) return session;

  // PWA/Safari isolation: localStorage may be empty but cookie has refresh token
  const rt = getRefreshTokenCookie();
  if (!rt) return null;

  const { data, error } = await supabaseAuth.auth.refreshSession({ refresh_token: rt });
  if (error || !data.session) {
    deleteRefreshTokenCookie();
    return null;
  }
  return data.session;
}

export async function signOut() {
  await supabaseAuth.auth.signOut();
}

export async function mergeAccounts(keepUserId: number, mergeFromUserId: number) {
  // Transfer all data from mergeFromUserId → keepUserId, then delete mergeFrom

  // 1. Tournament registrations — re-assign or skip if duplicate
  const { data: regs } = await supabase.from('tournament_registrations')
    .select('id, tournament_id').eq('user_id', mergeFromUserId);
  for (const reg of regs || []) {
    const { data: existing } = await supabase.from('tournament_registrations')
      .select('id').eq('tournament_id', reg.tournament_id).eq('user_id', keepUserId).maybeSingle();
    if (!existing) {
      await supabase.from('tournament_registrations').update({ user_id: keepUserId }).eq('id', reg.id);
    }
  }

  // 2. Tournament results — re-assign or skip if duplicate
  const { data: results } = await supabase.from('tournament_results')
    .select('id, tournament_id').eq('user_id', mergeFromUserId);
  for (const res of results || []) {
    const { data: existing } = await supabase.from('tournament_results')
      .select('id').eq('tournament_id', res.tournament_id).eq('user_id', keepUserId).maybeSingle();
    if (!existing) {
      await supabase.from('tournament_results').update({ user_id: keepUserId }).eq('id', res.id);
    }
  }

  // 3. Waitlist — re-assign or skip
  const { data: wl } = await supabase.from('tournament_waitlist')
    .select('id, tournament_id').eq('user_id', mergeFromUserId);
  for (const w of wl || []) {
    const { data: existing } = await supabase.from('tournament_waitlist')
      .select('id').eq('tournament_id', w.tournament_id).eq('user_id', keepUserId).maybeSingle();
    if (!existing) {
      await supabase.from('tournament_waitlist').update({ user_id: keepUserId }).eq('id', w.id);
    }
  }

  // 4. Orders — re-assign all
  await supabase.from('orders').update({ user_id: keepUserId }).eq('user_id', mergeFromUserId);

  // 5. Purchases — re-assign or skip duplicates
  const { data: purchases } = await supabase.from('user_purchases')
    .select('id, item_id').eq('user_id', mergeFromUserId);
  for (const p of purchases || []) {
    const { data: existing } = await supabase.from('user_purchases')
      .select('id').eq('user_id', keepUserId).eq('item_id', p.item_id).maybeSingle();
    if (!existing) {
      await supabase.from('user_purchases').update({ user_id: keepUserId }).eq('id', p.id);
    }
  }

  // 6. Merge player_ratings — sum up stats
  const { data: keepRating } = await supabase.from('player_ratings').select('*').eq('user_id', keepUserId).maybeSingle();
  const { data: mergeRating } = await supabase.from('player_ratings').select('*').eq('user_id', mergeFromUserId).maybeSingle();

  if (mergeRating && keepRating) {
    await supabase.from('player_ratings').update({
      total_tournaments: (keepRating.total_tournaments || 0) + (mergeRating.total_tournaments || 0),
      total_points: (keepRating.total_points || 0) + (mergeRating.total_points || 0),
      current_rating: (keepRating.current_rating || 0) + (mergeRating.current_rating || 0),
      monthly_rating: (keepRating.monthly_rating || 0) + (mergeRating.monthly_rating || 0),
      wins: (keepRating.wins || 0) + (mergeRating.wins || 0),
      final_tables: (keepRating.final_tables || 0) + (mergeRating.final_tables || 0),
      top_10_finishes: (keepRating.top_10_finishes || 0) + (mergeRating.top_10_finishes || 0),
      top_4_finishes: (keepRating.top_4_finishes || 0) + (mergeRating.top_4_finishes || 0),
      top_3_finishes: (keepRating.top_3_finishes || 0) + (mergeRating.top_3_finishes || 0),
      total_knockouts: (keepRating.total_knockouts || 0) + (mergeRating.total_knockouts || 0),
      xp: (keepRating.xp || 0) + (mergeRating.xp || 0),
      coins_balance: (keepRating.coins_balance || 0) + (mergeRating.coins_balance || 0),
      best_finish: Math.min(keepRating.best_finish || 999, mergeRating.best_finish || 999),
    }).eq('user_id', keepUserId);
  } else if (mergeRating && !keepRating) {
    await supabase.from('player_ratings').update({ user_id: keepUserId }).eq('user_id', mergeFromUserId);
  }

  // 7. Mark old account as merged (can't DELETE via anon RLS, so we deactivate it)
  // Clear email so it doesn't conflict, mark as guest
  await supabase.from('users').update({
    email: null,
    auth_uid: null,
    is_guest: true,
    display_name: `[merged into #${keepUserId}]`,
  }).eq('id', mergeFromUserId);
}

export async function upsertUserByEmail(email: string, authUid: string) {
  // Users-table upsert via SECURITY DEFINER RPC (see supabase-security.sql) so
  // anon never needs direct access to auth_uid / telegram_id. The RPC handles
  // the concurrent-signup race atomically via ON CONFLICT (email).
  const { data: row, error } = await supabase.rpc('login_with_email', {
    p_email: email,
    p_auth_uid: authUid,
  });
  if (error) throw error;
  const { is_new: isNew, ...user } = (row ?? {}) as any;

  await supabase.from('player_ratings')
    .upsert({ user_id: user.id, current_rating: 0, monthly_rating: 0, coins_balance: 5 }, { onConflict: 'user_id', ignoreDuplicates: true });
  await _giveDefaultItems(user.id);

  if (isNew) {
    await _linkReferral(user.id);
  } else {
    // Grant win-unlocked skins (parity with the Telegram path) so existing wins
    // are reflected even if the win was recorded while logged out.
    const { data: prWins } = await supabase.from('player_ratings').select('wins').eq('user_id', user.id).maybeSingle();
    await _giveWinItems(user.id, prWins?.wins ?? 0);
  }

  return user;
}

/* ═══════════════════════════════════════════════════════
   USERS
   ═══════════════════════════════════════════════════════ */

const DEFAULT_CHAR = 'char_pers_12';
const DEFAULT_BG   = 'bg_back8';

// Items unlocked by wins: { itemId -> winsRequired }
const WIN_ITEMS: Record<string, number> = {
  'char_pers_11': 1,
  'char_pers_4':  5,
};

async function _giveWinItems(userId: number, wins: number) {
  const toGive = Object.entries(WIN_ITEMS)
    .filter(([, req]) => wins >= req)
    .map(([id]) => ({ user_id: userId, item_id: id, price: 0 }));
  if (toGive.length === 0) return;
  await supabase.from('user_purchases')
    .upsert(toGive, { onConflict: 'user_id,item_id', ignoreDuplicates: true });
}

async function _giveDefaultItems(userId: number) {
  // Add to purchases (ignore if already owned)
  await supabase.from('user_purchases')
    .upsert([
      { user_id: userId, item_id: DEFAULT_CHAR, price: 0 },
      { user_id: userId, item_id: DEFAULT_BG,   price: 0 },
    ], { onConflict: 'user_id,item_id', ignoreDuplicates: true });

  // Equip only if nothing is equipped yet
  const { data: pr } = await supabase
    .from('player_ratings').select('equipped_char, equipped_bg').eq('user_id', userId).maybeSingle();
  const updates: Record<string, string> = {};
  if (!pr?.equipped_char) updates.equipped_char = DEFAULT_CHAR;
  if (!pr?.equipped_bg)   updates.equipped_bg   = DEFAULT_BG;
  if (Object.keys(updates).length) {
    await supabase.from('player_ratings').update(updates).eq('user_id', userId);
  }
}

export async function upsertUser(data: {
  telegram_id: number; username?: string; first_name?: string; last_name?: string; phone?: string;
}) {
  const { telegram_id, username, first_name, last_name, phone } = data;

  // The users-table upsert goes through a SECURITY DEFINER RPC so that anon
  // never needs direct read access to telegram_id (see supabase-security.sql).
  // It returns only safe columns + is_new; phone/auth_uid are never sent back.
  const { data: row, error } = await supabase.rpc('login_with_telegram', {
    p_telegram_id: telegram_id,
    p_username: username ?? null,
    p_first_name: first_name ?? null,
    p_last_name: last_name ?? null,
    p_phone: phone ?? null,
  });
  if (error) throw error;
  const { is_new: isNew, ...user } = (row ?? {}) as any;

  // Ensure player_ratings record exists (may be missing for old users)
  const { data: hasRating } = await supabase
    .from('player_ratings').select('id').eq('user_id', user.id).maybeSingle();
  if (!hasRating) {
    await supabase.from('player_ratings')
      .insert({ user_id: user.id, current_rating: 0, monthly_rating: 0, coins_balance: 5 });
  }

  // Fire-and-forget: cosmetic items / referral don't gate login
  (async () => {
    try {
      await _giveDefaultItems(user.id);
      if (isNew) {
        await _linkReferral(user.id);
      } else {
        const { data: pr } = await supabase.from('player_ratings').select('wins').eq('user_id', user.id).maybeSingle();
        await _giveWinItems(user.id, pr?.wins ?? 0);
      }
    } catch { /* non-critical */ }
  })();

  return user;
}

// Admin user list — safe columns only. telegram_id / phone are not exposed to
// the client at all anymore (column REVOKE in supabase-security.sql); admins
// manage by name / username / email / id instead.
export async function getUsers() {
  const { data, error } = await supabase
    .from('users')
    .select(USER_SAFE_COLS)
    .order('created_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  return data;
}

export async function getUsersCount(): Promise<number> {
  const { count, error } = await supabase.from('users').select('id', { count: 'exact', head: true });
  if (error) throw error;
  return count ?? 0;
}

// Safe user columns for anon reads/returns — deliberately excludes
// telegram_id / phone / auth_uid (see supabase-security.sql). Never use
// select('*') on users from the anon client; after the column REVOKE it errors.
const USER_SAFE_COLS = 'id, username, first_name, last_name, display_name, name_moderation_status, pending_display_name, is_guest, invited_by, email, created_at, updated_at';

export async function getUserById(id: number) {
  const { data, error } = await supabase
    .from('users')
    .select(USER_SAFE_COLS)
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

export async function getPendingNameModerations() {
  const { data, error } = await supabase
    .from('users')
    .select('id, username, first_name, last_name, display_name, pending_display_name, name_moderation_status, created_at, updated_at')
    .eq('name_moderation_status', 'pending')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function submitNameForModeration(userId: number, displayName: string) {
  if (!displayName || displayName.trim().length === 0) throw new Error('Name cannot be empty');
  if (displayName.length > 100) throw new Error('Name too long');

  const { data, error } = await supabase.from('users')
    .update({ pending_display_name: displayName.trim(), name_moderation_status: 'pending', updated_at: moscowNow() })
    .eq('id', userId).select(USER_SAFE_COLS).single();
  if (error) throw error;
  return data;
}

export async function moderateName(userId: number, action: 'approve' | 'reject') {
  if (action === 'approve') {
    const { data: user } = await supabase.from('users').select('pending_display_name').eq('id', userId).single();
    if (!user?.pending_display_name) throw new Error('No pending name');

    const { data, error } = await supabase.from('users')
      .update({ display_name: user.pending_display_name, pending_display_name: null, name_moderation_status: 'approved', updated_at: moscowNow() })
      .eq('id', userId).select(USER_SAFE_COLS).single();
    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase.from('users')
      .update({ pending_display_name: null, name_moderation_status: 'rejected', updated_at: moscowNow() })
      .eq('id', userId).select(USER_SAFE_COLS).single();
    if (error) throw error;
    return data;
  }
}

export async function deleteUser(userId: number) {
  const tables = [
    { table: 'tournament_results', field: 'user_id' },
    { table: 'tournament_waitlist', field: 'user_id' },
    { table: 'tournament_registrations', field: 'user_id' },
    { table: 'user_purchases', field: 'user_id' },
    { table: 'player_ratings', field: 'user_id' },
    { table: 'bookings', field: 'user_id' },
    { table: 'orders', field: 'user_id' },
  ];
  await Promise.all(tables.map(async ({ table, field }) => {
    const { error } = await supabase.from(table).delete().eq(field, userId);
    if (error) throw new Error(`Failed to delete from ${table}: ${error.message}`);
  }));
  const { error } = await supabase.from('users').delete().eq('id', userId);
  if (error) throw error;
}

export async function createGuestUser(displayName: string, invitedBy?: number) {
  // Generate a unique fake telegram_id for guest (negative to avoid collisions)
  const fakeTelegramId = -Date.now() - Math.floor(Math.random() * 10000);

  const { data: newUser, error } = await supabase
    .from('users')
    .insert({
      telegram_id: fakeTelegramId,
      display_name: displayName.trim(),
      first_name: displayName.trim(),
      is_guest: true,
      invited_by: invitedBy || null,
    })
    .select(USER_SAFE_COLS).single();
  if (error) throw error;
  return newUser;
}

export async function searchUsers(query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  // Search by username (without @)
  const searchTerm = q.startsWith('@') ? q.slice(1) : q;

  const { data, error } = await supabase
    .from('users')
    .select('id, username, first_name, last_name, display_name, is_guest')
    .or(`username.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,display_name.ilike.%${searchTerm}%`)
    .limit(20);
  if (error) throw error;
  return data || [];
}

export async function adminRegisterForTournament(tournamentId: number, userId: number) {
  // Direct insert bypassing registration_open check — admin privilege
  const { data: existing } = await supabase
    .from('tournament_registrations')
    .select('id, status')
    .eq('tournament_id', tournamentId)
    .eq('user_id', userId)
    .maybeSingle();

  if (existing) {
    if (existing.status === 'registered') throw new Error('Уже зарегистрирован');
    // Re-activate cancelled registration
    const { error } = await supabase
      .from('tournament_registrations')
      .update({ status: 'registered', registered_at: moscowNow() })
      .eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('tournament_registrations')
      .insert({ tournament_id: tournamentId, user_id: userId, status: 'registered' });
    if (error) throw error;
  }

  // Update current_players count
  const { count } = await supabase
    .from('tournament_registrations')
    .select('*', { count: 'exact', head: true })
    .eq('tournament_id', tournamentId)
    .eq('status', 'registered');

  await supabase.from('tournaments').update({
    current_players: count || 0,
    is_sold_out: false, // admin override
  }).eq('id', tournamentId);
}

export async function adminUnregisterFromTournament(tournamentId: number, userId: number) {
  // Cancel registration and reset check-in
  const { error } = await supabase
    .from('tournament_registrations')
    .update({ status: 'cancelled', checked_in: false, checked_in_at: null })
    .eq('tournament_id', tournamentId)
    .eq('user_id', userId)
    .eq('status', 'registered');
  if (error) throw error;

  // Update current_players count
  const { count } = await supabase
    .from('tournament_registrations')
    .select('*', { count: 'exact', head: true })
    .eq('tournament_id', tournamentId)
    .eq('status', 'registered');

  await supabase.from('tournaments').update({
    current_players: count || 0,
  }).eq('id', tournamentId);
}

export async function checkInPlayer(tournamentId: number, userId: number, buyInPrice: number) {
  // 1. Verify registration
  const { data: reg } = await supabase
    .from('tournament_registrations')
    .select('id, checked_in')
    .eq('tournament_id', tournamentId)
    .eq('user_id', userId)
    .eq('status', 'registered')
    .maybeSingle();

  if (!reg) throw new Error('Игрок не зарегистрирован на этот турнир');
  if (reg.checked_in) throw new Error('Игрок уже отмечен');

  // Weekly-streak visits before this check-in (to tell whether it adds a new visit day).
  const visitsBefore = await getLoyaltyStatus(userId).then(s => s.visits).catch(() => null);

  // 2. Mark as checked in
  const { error } = await supabase
    .from('tournament_registrations')
    .update({ checked_in: true, checked_in_at: moscowNow() })
    .eq('id', reg.id);
  if (error) throw error;

  // 3. Check for free entry card
  const { data: pr } = await supabase.from('player_ratings').select('free_entries').eq('user_id', userId).single();
  const hasFreeEntry = (pr?.free_entries || 0) > 0;

  // 4. Create buy-in order automatically (only if no existing buy-in order)
  const { data: existingOrder } = await supabase.from('orders')
    .select('id')
    .eq('user_id', userId)
    .eq('tournament_id', tournamentId)
    .ilike('items', '%Стартовый стек%')
    .neq('status', 'cancelled')
    .maybeSingle();

  if (!existingOrder) {
    // Free-entry cards are auto-spent EXCEPT when the buy-in is 0₽ (free entry,
    // nothing to cover) or 1500₽ (card intentionally does not apply here).
    const useCard = hasFreeEntry && buyInPrice !== 0 && buyInPrice !== 1500;
    const itemName = useCard ? 'Стартовый стек (карточка)' : 'Стартовый стек';

    if (buyInPrice > 0 || useCard) {
      await supabase.from('orders').insert({
        user_id: userId,
        items: JSON.stringify([{ name: itemName, price: buyInPrice, quantity: 1 }]),
        total_price: buyInPrice,
        tournament_id: tournamentId,
        status: 'delivered',
        paid: useCard,
        hucoin_rate: HUCOIN_RATES.game, pay_class: 'game',
      });
    }

    // Deduct free entry card only when it was actually applied
    if (useCard) {
      await supabase.from('player_ratings').update({
        free_entries: Math.max(0, (pr?.free_entries || 0) - 1),
      }).eq('user_id', userId);
    }
  }

  // 5. Referral reward is processed automatically by DB trigger trg_process_referral_on_checkin
  // 6. Weekly-streak bonus is awarded by DB trigger trg_loyalty_checkin; here we only notify.
  const loyalty = await afterCheckIn(userId, visitsBefore);

  const newVisit = visitsBefore !== null && !!loyalty && loyalty.visits > visitsBefore;
  return { success: true, loyalty: newVisit ? loyalty : null };
}

export async function hasPendingReentry(tournamentId: number, userId: number): Promise<boolean> {
  const { data } = await supabase
    .from('orders')
    .select('id')
    .eq('user_id', userId)
    .eq('tournament_id', tournamentId)
    .eq('status', 'pending')
    .ilike('items', '%Ре-ентри%')
    .maybeSingle();
  return !!data;
}

// Player-initiated re-entry: creates pending order (goes to admin queue)
export async function createRebuyOrder(tournamentId: number, userId: number, buyInPrice: number, tableNumber?: string, useCard?: boolean) {
  // Prevent double re-entry: only allow if no pending re-entry exists
  if (tableNumber) {
    const pending = await hasPendingReentry(tournamentId, userId);
    if (pending) throw new Error('У вас уже есть ожидающий ре-ентри. Дождитесь его доставки.');
  }

  // Same rule as check-in: a card is never spent when the price is 0₽ or 1500₽.
  const applyCard = !!useCard && buyInPrice !== 0 && buyInPrice !== 1500;
  const itemName = applyCard ? 'Ре-ентри (карточка)' : 'Ре-ентри';

  await supabase.from('orders').insert({
    user_id: userId,
    items: JSON.stringify([{ name: itemName, price: buyInPrice, quantity: 1 }]),
    total_price: buyInPrice,
    tournament_id: tournamentId,
    table_number: tableNumber || null,
    status: tableNumber ? 'pending' : 'delivered',
    paid: applyCard,
    hucoin_rate: HUCOIN_RATES.game, pay_class: 'game',
  });

  if (applyCard) {
    const { data: pr } = await supabase.from('player_ratings').select('free_entries').eq('user_id', userId).single();
    if (pr) {
      await supabase.from('player_ratings').update({
        free_entries: Math.max(0, (pr.free_entries || 0) - 1),
      }).eq('user_id', userId);
    }
  }
}

export async function undoCheckIn(tournamentId: number, userId: number) {
  const { error } = await supabase
    .from('tournament_registrations')
    .update({ checked_in: false, checked_in_at: null })
    .eq('tournament_id', tournamentId)
    .eq('user_id', userId);
  if (error) throw error;

  // Find the buy-in order before cancelling it
  const { data: buyInOrder } = await supabase.from('orders')
    .select('id, items')
    .eq('user_id', userId)
    .eq('tournament_id', tournamentId)
    .ilike('items', '%Стартовый стек%')
    .neq('status', 'cancelled')
    .maybeSingle();

  if (buyInOrder) {
    await supabase.from('orders').update({ status: 'cancelled' }).eq('id', buyInOrder.id);

    // Restore free entry if it was used (order name contains 'карточка')
    const items = typeof buyInOrder.items === 'string' ? JSON.parse(buyInOrder.items) : (buyInOrder.items || []);
    const usedFreeEntry = items.some((i: any) => i.name?.includes('карточка'));
    if (usedFreeEntry) {
      const { data: pr } = await supabase.from('player_ratings').select('free_entries').eq('user_id', userId).single();
      if (pr) {
        await supabase.from('player_ratings').update({
          free_entries: (pr.free_entries || 0) + 1,
        }).eq('user_id', userId);
      }
    }
  }
}

export async function decodeQR(qrData: string) {
  let parsed: any;
  try { parsed = JSON.parse(qrData); } catch { throw new Error('Invalid QR format'); }
  const { username, tournament_id } = parsed;
  if (!username || !tournament_id) throw new Error('QR must contain username and tournament_id');

  const { data: user, error: ue } = await supabase
    .from('users').select('id, username, first_name, last_name, display_name').eq('username', username).single();
  if (ue || !user) throw new Error('User not found');

  const { data: tournament, error: te } = await supabase
    .from('tournaments').select('id, name, start_date').eq('id', tournament_id).single();
  if (te || !tournament) throw new Error('Tournament not found');

  return { user, tournament };
}

/* ═══════════════════════════════════════════════════════
   TOURNAMENTS
   ═══════════════════════════════════════════════════════ */

export async function getTournaments(limit = 50) {
  const { data, error } = await supabase.from('tournaments').select('*')
    .order('start_date', { ascending: false }).limit(limit);
  if (error) throw error;
  return data;
}

export async function getTournamentById(id: number) {
  const { data: t, error } = await supabase.from('tournaments').select('*').eq('id', id).single();
  if (error || !t) throw error || new Error('Tournament not found');

  const [{ count: regCount }, { count: waitCount }] = await Promise.all([
    supabase.from('tournament_registrations').select('*', { count: 'exact', head: true }).eq('tournament_id', id).eq('status', 'registered'),
    supabase.from('tournament_waitlist').select('*', { count: 'exact', head: true }).eq('tournament_id', id),
  ]);

  const cp = regCount || 0;
  return { ...t, current_players: cp, waitlist_count: waitCount || 0, is_sold_out: cp >= t.max_players };
}

export async function createTournament(data: {
  name: string; tournament_type: string; start_date: string;
  location?: string | null; description?: string | null; max_players: number;
  guarantee_points?: number | null; features?: string | null; dress_code?: string | null;
  has_bounty?: boolean; bounty_points?: number; buy_in_price?: number; reentry_price?: number;
  prize_distribution?: number[] | null; is_mpt?: boolean;
  is_tv?: boolean; tv_series_id?: number | null;
}) {
  if (!data.name || data.name.trim().length === 0) throw new Error('Название турнира обязательно');
  if (data.name.length > 200) throw new Error('Название слишком длинное');
  if (!data.max_players || data.max_players < 2) throw new Error('Минимум 2 игрока');
  if (!data.start_date) throw new Error('Дата начала обязательна');

  const { data: t, error } = await supabase.from('tournaments').insert({
    name: data.name.trim(), tournament_type: data.tournament_type, start_date: data.start_date,
    location: data.location || null, description: data.description || null,
    max_players: data.max_players,
    guarantee_points: data.guarantee_points || null, features: data.features || null,
    dress_code: data.dress_code || null,
    has_bounty: !!data.has_bounty, bounty_points: data.bounty_points || 0,
    buy_in_price: data.buy_in_price || 0,
    reentry_price: data.reentry_price ?? data.buy_in_price ?? 0,
    prize_distribution: data.prize_distribution || null,
    is_mpt: !!data.is_mpt,
    is_tv: !!data.is_tv,
    tv_series_id: data.is_tv ? (data.tv_series_id ?? null) : null,
  }).select().single();
  if (error) throw error;
  return t;
}

export async function updateTournament(id: number, data: Record<string, any>) {
  const updates: Record<string, unknown> = {};
  if (data.registration_open !== undefined) updates.registration_open = !!data.registration_open;
  if (data.orders_open !== undefined) updates.orders_open = !!data.orders_open;
  if (data.is_finished !== undefined) {
    updates.is_finished = !!data.is_finished;
    if (data.is_finished) { updates.registration_open = false; updates.orders_open = false; }
  }
  if (data.has_bounty !== undefined) updates.has_bounty = !!data.has_bounty;
  if (data.bounty_points !== undefined) updates.bounty_points = data.bounty_points;
  if (data.is_mpt !== undefined) updates.is_mpt = !!data.is_mpt;
  if (data.is_tv !== undefined) {
    updates.is_tv = !!data.is_tv;
    updates.tv_series_id = data.is_tv ? (data.tv_series_id ?? null) : null;
  }
  for (const k of ['name', 'start_date', 'location', 'description', 'max_players', 'guarantee_points', 'features', 'dress_code', 'buy_in_price', 'reentry_price', 'prize_distribution']) {
    if (data[k] !== undefined) updates[k] = data[k];
  }

  const { data: updated, error } = await supabase.from('tournaments').update(updates).eq('id', id).select().single();
  if (error) throw error;
  return updated;
}

export async function deleteTournament(id: number) {
  await supabase.from('tournament_results').delete().eq('tournament_id', id);
  await supabase.from('tournament_waitlist').delete().eq('tournament_id', id);
  await supabase.from('tournament_registrations').delete().eq('tournament_id', id);
  await supabase.from('orders').delete().eq('tournament_id', id);
  const { error } = await supabase.from('tournaments').delete().eq('id', id);
  if (error) throw error;
}

/* ═══════════════════════════════════════════════════════
   TOURNAMENT REGISTRATIONS
   ═══════════════════════════════════════════════════════ */

export async function registerForTournament(tournamentId: number, userId: number) {
  const { data, error } = await supabase.rpc('register_for_tournament', {
    p_tournament_id: tournamentId,
    p_user_id: userId,
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);

  // Notify
  try {
    const { data: t } = await supabase.from('tournaments').select('name, start_date, max_players').eq('id', tournamentId).single();
    if (t) {
      const date = new Date(t.start_date).toLocaleDateString('ru-RU', { timeZone: 'Europe/Moscow', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
      if (data?.status === 'registered') {
        notify.notifyRegistrationConfirmed(userId, t.name, date);

        // Check if 5 spots left — notify all
        const { count } = await supabase.from('tournament_registrations')
          .select('*', { count: 'exact', head: true })
          .eq('tournament_id', tournamentId).eq('status', 'registered');
        const spotsLeft = t.max_players - (count || 0);
        if (spotsLeft === 5) {
          notify.notifySpotsAlmostFull(t.name, spotsLeft);
        }
      } else if (data?.status === 'waitlist') {
        notify.notifyWaitlisted(userId, t.name, data.position || 0);
      }
    }
  } catch {}

  return data;
}

async function promoteFromWaitlist(tournamentId: number) {
  const { data: t } = await supabase.from('tournaments')
    .select('max_players, name').eq('id', tournamentId).single();
  if (!t) return;

  const { count: currentCount } = await supabase.from('tournament_registrations')
    .select('*', { count: 'exact', head: true })
    .eq('tournament_id', tournamentId).eq('status', 'registered');
  if ((currentCount || 0) >= t.max_players) return; // still full

  const { data: first } = await supabase.from('tournament_waitlist')
    .select('*').eq('tournament_id', tournamentId)
    .order('position', { ascending: true }).limit(1).maybeSingle();
  if (!first) return; // no one waiting

  // Register the first person from waitlist
  await supabase.from('tournament_registrations').insert({
    tournament_id: tournamentId,
    user_id: first.user_id,
    status: 'registered',
    registered_at: moscowNow(),
  });

  // Remove them from waitlist
  await supabase.from('tournament_waitlist').delete().eq('id', first.id);

  // Reorder remaining positions
  const { data: remaining } = await supabase.from('tournament_waitlist')
    .select('id').eq('tournament_id', tournamentId)
    .order('position', { ascending: true });
  if (remaining && remaining.length > 0) {
    await Promise.all(
      remaining.map((r: any, i: number) =>
        supabase.from('tournament_waitlist').update({ position: i + 1 }).eq('id', r.id)
      )
    );
  }

  notify.notifyPromotedFromWaitlist(first.user_id, t.name);
}

export async function cancelRegistration(tournamentId: number, userId: number) {
  const { data, error } = await supabase.rpc('cancel_tournament_registration', {
    p_tournament_id: tournamentId,
    p_user_id: userId,
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);

  try {
    const { data: t } = await supabase.from('tournaments').select('name').eq('id', tournamentId).single();
    if (t) notify.notifyRegistrationCancelled(userId, t.name);
    await promoteFromWaitlist(tournamentId);
  } catch {}

  return data;
}

export async function getRegistrationStatus(tournamentId: number, userId: number): Promise<{ status: 'registered' | 'waitlist' | 'not_registered'; position?: number; checked_in?: boolean }> {
  const { data: reg } = await supabase.from('tournament_registrations')
    .select('*, checked_in').eq('tournament_id', tournamentId).eq('user_id', userId).eq('status', 'registered').maybeSingle();
  if (reg) return { status: 'registered', checked_in: !!reg.checked_in };

  const { data: wl } = await supabase.from('tournament_waitlist')
    .select('*').eq('tournament_id', tournamentId).eq('user_id', userId).maybeSingle();
  if (wl) return { status: 'waitlist', position: wl.position };

  return { status: 'not_registered' };
}

export async function getTournamentRegistrations(tournamentId: number) {
  const { data, error } = await supabase.from('tournament_registrations')
    .select('*, users(first_name, last_name, username, display_name, is_guest, invited_by, email)')
    .eq('tournament_id', tournamentId).eq('status', 'registered')
    .order('registered_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((r: any) => ({ ...r, first_name: r.users?.first_name, last_name: r.users?.last_name, username: r.users?.username, display_name: r.users?.display_name, is_guest: r.users?.is_guest, invited_by: r.users?.invited_by, email: r.users?.email, checked_in: r.checked_in, checked_in_at: r.checked_in_at, early_bird: r.early_bird, early_bird_at: r.early_bird_at, users: undefined }));
}

/* ═══════════════════════════════════════════════════════
   EARLY BIRD ("Ранняя пташка") — admins mark players who arrived on time.
   Marks live on tournament_registrations and are counted per rating series.
   ═══════════════════════════════════════════════════════ */

export async function setEarlyBird(tournamentId: number, userId: number, value: boolean) {
  const { error } = await supabase
    .from('tournament_registrations')
    .update({ early_bird: value, early_bird_at: value ? moscowNow() : null })
    .eq('tournament_id', tournamentId)
    .eq('user_id', userId);
  if (error) throw error;
}

// Admin stats: one row per non-guest player with their early-bird count and the
// dated list of tournaments where they were marked, within a series date range.
export async function getSeriesEarlyBirdStats(startDate: string, endDateExclusive: string) {
  const { data, error } = await supabase
    .from('tournament_registrations')
    .select('user_id, early_bird_at, tournaments!inner(id, name, start_date), users!inner(display_name, first_name, last_name, username, is_guest)')
    .eq('early_bird', true)
    .gte('tournaments.start_date', startDate)
    .lt('tournaments.start_date', endDateExclusive);
  if (error) throw error;

  const byUser = new Map<number, any>();
  for (const r of (data as any[]) || []) {
    if (r.users?.is_guest) continue;
    let u = byUser.get(r.user_id);
    if (!u) {
      u = {
        user_id: r.user_id,
        name: r.users?.display_name || `${r.users?.first_name || ''} ${r.users?.last_name || ''}`.trim() || r.users?.username || `#${r.user_id}`,
        username: r.users?.username || null,
        count: 0,
        marks: [] as Array<{ tournament_id: number; name: string; date: string }>,
      };
      byUser.set(r.user_id, u);
    }
    u.count += 1;
    u.marks.push({ tournament_id: r.tournaments?.id, name: r.tournaments?.name, date: r.tournaments?.start_date });
  }
  return Array.from(byUser.values())
    .map(u => ({ ...u, marks: u.marks.sort((a: any, b: any) => (a.date < b.date ? -1 : 1)) }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

// Player profile: number of early-bird marks the user earned within a series.
export async function getUserEarlyBirdCount(userId: number, startDate: string, endDateExclusive: string): Promise<number> {
  const { data, error } = await supabase
    .from('tournament_registrations')
    .select('id, tournaments!inner(start_date)')
    .eq('user_id', userId)
    .eq('early_bird', true)
    .gte('tournaments.start_date', startDate)
    .lt('tournaments.start_date', endDateExclusive);
  if (error) throw error;
  return (data || []).length;
}

/* ═══════════════════════════════════════════════════════
   ATTENDANCE / RETENTION — per-player check-in history & metrics.
   A "visit" = a distinct day the player checked in to a tournament.
   ═══════════════════════════════════════════════════════ */
export interface AttendanceRow {
  user_id: number;
  name: string;
  username: string | null;
  count: number;               // distinct visit days
  lastVisit: string | null;    // YYYY-MM-DD of most recent visit
  lastDaysAgo: number | null;  // days since last visit
  maxGap: number;              // largest gap (days) between consecutive visits
  days: string[];              // sorted distinct visit days (for the calendar)
}

export async function getAttendanceStats(fromDate?: string, toDate?: string): Promise<AttendanceRow[]> {
  // Paginated: check-ins across all time can exceed the 1000-row cap.
  // Optional date range filters which tournaments' check-ins are counted.
  const rows = await fetchAllPaginated<any>(() => {
    let q = supabase
      .from('tournament_registrations')
      .select('user_id, tournaments!inner(start_date), users!inner(display_name, first_name, last_name, username, is_guest)')
      .eq('checked_in', true);
    if (fromDate) q = q.gte('tournaments.start_date', fromDate);
    if (toDate) q = q.lte('tournaments.start_date', `${toDate}T23:59:59`);
    return q.order('id', { ascending: true });
  });

  const DAY = 86400000;
  const epochDay = (d: string) => Math.floor(Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10)) / DAY);
  const todayDay = Math.floor(Date.now() / DAY);

  const byUser = new Map<number, { user_id: number; name: string; username: string | null; days: Set<string> }>();
  for (const r of rows) {
    if (r.users?.is_guest) continue;
    const day = (r.tournaments?.start_date || '').slice(0, 10);
    if (!day) continue;
    let u = byUser.get(r.user_id);
    if (!u) {
      u = {
        user_id: r.user_id,
        name: r.users?.display_name || `${r.users?.first_name || ''} ${r.users?.last_name || ''}`.trim() || r.users?.username || `#${r.user_id}`,
        username: r.users?.username || null,
        days: new Set(),
      };
      byUser.set(r.user_id, u);
    }
    u.days.add(day);
  }

  return Array.from(byUser.values()).map(u => {
    const days = Array.from(u.days).sort();
    const count = days.length;
    const lastVisit = days[count - 1] || null;
    const lastDaysAgo = lastVisit ? Math.max(0, todayDay - epochDay(lastVisit)) : null;
    let maxGap = 0;
    for (let i = 1; i < days.length; i++) maxGap = Math.max(maxGap, epochDay(days[i]) - epochDay(days[i - 1]));
    return { user_id: u.user_id, name: u.name, username: u.username, count, lastVisit, lastDaysAgo, maxGap, days };
  }).sort((a, b) => b.count - a.count);
}

/* ═══════════════════════════════════════════════════════
   TEAMS / squad competition
   ═══════════════════════════════════════════════════════ */
export interface TeamMember { user_id: number; name: string; username: string | null; equipped_char: string | null; isCaptain: boolean; points: number }
export interface TeamInfo {
  id: number; name: string; color: string; suit: string;
  captain_user_id: number | null; captainName: string | null;
  points: number; registration_open: boolean; member_limit: number | null;
  sort_order: number;
  members: TeamMember[];
}

export async function getTeams(): Promise<TeamInfo[]> {
  const { data: teams, error } = await supabase.from('teams').select('*').order('sort_order');
  if (error) throw error;
  const members = await fetchAllPaginated<any>(() => supabase
    .from('player_ratings')
    .select('user_id, team_id, team_points, equipped_char, users!inner(display_name, first_name, last_name, username, is_guest)')
    .not('team_id', 'is', null)
    .order('user_id', { ascending: true }));
  const nm = (u: any, id: number) => u?.display_name || `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || u?.username || `#${id}`;
  const captainIds = (teams || []).map((t: any) => t.captain_user_id).filter(Boolean);
  const { data: capUsers } = captainIds.length
    ? await supabase.from('users').select('id, display_name, first_name, last_name, username').in('id', captainIds)
    : { data: [] as any[] };
  const capMap = new Map((capUsers || []).map((u: any) => [u.id, nm(u, u.id)]));
  const byTeam = new Map<number, TeamMember[]>();
  for (const m of members) {
    if (m.users?.is_guest) continue;
    const arr = byTeam.get(m.team_id) || [];
    arr.push({ user_id: m.user_id, name: nm(m.users, m.user_id), username: m.users?.username || null, equipped_char: m.equipped_char || null, isCaptain: false, points: m.team_points || 0 });
    byTeam.set(m.team_id, arr);
  }
  return (teams || []).map((t: any) => {
    const mem = (byTeam.get(t.id) || [])
      .map(x => ({ ...x, isCaptain: x.user_id === t.captain_user_id }))
      .sort((a, b) => b.points - a.points || (b.isCaptain ? 1 : 0) - (a.isCaptain ? 1 : 0) || a.name.localeCompare(b.name));
    const total = mem.reduce((s, m) => s + m.points, 0);
    return {
      id: t.id, name: t.name, color: t.color, suit: t.suit,
      captain_user_id: t.captain_user_id,
      captainName: t.captain_user_id ? (capMap.get(t.captain_user_id) || null) : null,
      points: total, registration_open: t.registration_open, member_limit: t.member_limit ?? null,
      sort_order: t.sort_order, members: mem,
    };
  });
}

// Lightweight summary for the home screen: the user's team + its rank, if any.
export async function getMyTeamSummary(userId: number): Promise<{ team: { id: number; name: string; color: string; suit: string; points: number } | null; rank: number | null; total: number }> {
  const [{ data: pr }, { data: teams }, members] = await Promise.all([
    supabase.from('player_ratings').select('team_id').eq('user_id', userId).maybeSingle(),
    supabase.from('teams').select('id, name, color, suit').order('sort_order'),
    fetchAllPaginated<any>(() => supabase.from('player_ratings').select('team_id, team_points').not('team_id', 'is', null).order('user_id', { ascending: true })),
  ]);
  const totals = new Map<number, number>();
  for (const m of members) totals.set(m.team_id, (totals.get(m.team_id) || 0) + (m.team_points || 0));
  const ranked = (teams || []).map((t: any) => ({ id: t.id, name: t.name, color: t.color, suit: t.suit, points: totals.get(t.id) || 0 }))
    .sort((a, b) => b.points - a.points || a.id - b.id);
  if (!pr?.team_id) return { team: null, rank: null, total: ranked.length };
  const idx = ranked.findIndex(t => t.id === pr.team_id);
  return { team: idx >= 0 ? ranked[idx] : null, rank: idx >= 0 ? idx + 1 : null, total: ranked.length };
}

// Ensure a player_ratings row exists, so team_id UPDATEs actually hit a row
// (users who never fully logged in may lack one → otherwise a silent no-op).
async function ensurePlayerRating(userId: number) {
  const { data } = await supabase.from('player_ratings').select('user_id').eq('user_id', userId).maybeSingle();
  if (!data) await supabase.from('player_ratings').insert({ user_id: userId, current_rating: 0, monthly_rating: 0, coins_balance: 0 });
}

export async function joinTeam(userId: number, teamId: number) {
  const { data: team } = await supabase.from('teams').select('registration_open, member_limit').eq('id', teamId).single();
  if (!team?.registration_open) throw new Error('Запись в эту команду закрыта');
  if (team.member_limit != null) {
    const { count } = await supabase.from('player_ratings').select('user_id', { count: 'exact', head: true }).eq('team_id', teamId);
    if ((count || 0) >= team.member_limit) throw new Error('В команде нет свободных мест');
  }
  await ensurePlayerRating(userId);
  const { error } = await supabase.from('player_ratings').update({ team_id: teamId }).eq('user_id', userId);
  if (error) throw error;
}

export async function leaveTeam(userId: number) {
  const { error } = await supabase.from('player_ratings').update({ team_id: null }).eq('user_id', userId);
  if (error) throw error;
}

// Admin — points are awarded per player; a team's total is the sum of members.
export async function addPlayerPoints(userId: number, delta: number, note?: string) {
  const { data: pr } = await supabase.from('player_ratings').select('team_points, team_id').eq('user_id', userId).single();
  const { error } = await supabase.from('player_ratings').update({ team_points: (pr?.team_points || 0) + delta }).eq('user_id', userId);
  if (error) throw error;
  await supabase.from('team_points_log').insert({ team_id: pr?.team_id ?? null, user_id: userId, delta, note: note || null });
}
export async function setTeamRegistration(teamId: number, open: boolean) {
  const { error } = await supabase.from('teams').update({ registration_open: open }).eq('id', teamId);
  if (error) throw error;
}
export async function setTeamLimit(teamId: number, limit: number | null) {
  const { error } = await supabase.from('teams').update({ member_limit: limit }).eq('id', teamId);
  if (error) throw error;
}
export async function adminSetPlayerTeam(userId: number, teamId: number | null) {
  if (teamId != null) await ensurePlayerRating(userId);
  const { error } = await supabase.from('player_ratings').update({ team_id: teamId }).eq('user_id', userId);
  if (error) throw error;
}
export async function getTeamPointsLog(): Promise<Array<{ id: number; team_id: number; user_id: number | null; userName: string | null; delta: number; note: string | null; created_at: string }>> {
  const { data, error } = await supabase.from('team_points_log').select('*').order('created_at', { ascending: false }).limit(200);
  if (error) throw error;
  const rows = data || [];
  const ids = [...new Set(rows.map((r: any) => r.user_id).filter(Boolean))];
  const { data: users } = ids.length
    ? await supabase.from('users').select('id, display_name, first_name, last_name, username').in('id', ids)
    : { data: [] as any[] };
  const um = new Map((users || []).map((u: any) => [u.id, u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || `#${u.id}`]));
  return rows.map((r: any) => ({ id: r.id, team_id: r.team_id, user_id: r.user_id ?? null, userName: r.user_id ? (um.get(r.user_id) || null) : null, delta: r.delta, note: r.note, created_at: r.created_at }));
}

export async function getTournamentWaitlist(tournamentId: number) {
  const { data, error } = await supabase.from('tournament_waitlist')
    .select('*, users(first_name, last_name, username, display_name)')
    .eq('tournament_id', tournamentId).order('position', { ascending: true });
  if (error) throw error;
  return (data || []).map((r: any) => ({ ...r, first_name: r.users?.first_name, last_name: r.users?.last_name, username: r.users?.username, display_name: r.users?.display_name, users: undefined }));
}

/* ═══════════════════════════════════════════════════════
   TOURNAMENT RESULTS
   ═══════════════════════════════════════════════════════ */

// Recompute aggregate counters in player_ratings from tournament_results.
// Used after editing a previously-saved result so totals stay in sync with the
// source of truth. Does NOT touch monthly_rating (resets monthly), xp /
// current_level / current_streak / coins_balance (already-claimed rewards).
async function recomputeAggregatesFromResults(userId: number) {
  // Placement stats (wins, top_X, best_finish) come ONLY from entered results,
  // counted by final position. Auto-generated participation placeholders
  // (0 points AND finished at the field size) are excluded — they represent a
  // check-in without an entered result. Real busts out of the money (0 points
  // but a real position) still count.
  const { data: results } = await supabase
    .from('tournament_results')
    .select('final_position, knockouts_count, points_earned, tournaments!inner(total_players)')
    .eq('user_id', userId);
  const list = results || [];
  let total_knockouts = 0, total_points = 0, wins = 0;
  let top_3 = 0, top_4 = 0, final_tables = 0, top_10 = 0;
  let best_finish: number | null = null;
  for (const r of list) {
    total_knockouts += r.knockouts_count || 0;
    total_points += r.points_earned || 0;
    const tp = (r as any).tournaments?.total_players;
    const isPlaceholder = !(r.points_earned > 0) && tp != null && r.final_position === tp;
    if (isPlaceholder) continue;
    const fp = r.final_position;
    if (fp === 1) wins++;
    if (fp <= 3) top_3++;
    if (fp <= 4) top_4++;
    if (fp <= 9) final_tables++;
    if (fp <= 18) top_10++;
    if (best_finish === null || fp < best_finish) best_finish = fp;
  }

  // Played-tournament count = number of check-ins (counts even when no result
  // was entered for the player).
  const { count: checkinCount } = await supabase
    .from('tournament_registrations')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('checked_in', true);

  await supabase.from('player_ratings').update({
    total_tournaments: checkinCount || 0,
    total_knockouts,
    total_points,
    current_rating: Math.max(0, total_points),
    wins,
    top_3_finishes: top_3,
    top_4_finishes: top_4,
    final_tables,
    top_10_finishes: top_10,
    best_finish,
  }).eq('user_id', userId);
}

async function processResult(
  tournamentId: number, user_id: number, final_position: number, entries_count: number,
  knockouts_count: number, total_players: number, tournament: any
) {
  const result: TournamentResult = {
    tournamentId, userId: user_id, finalPosition: final_position,
    totalPlayers: total_players,
    tournamentType: tournament.tournament_type as any, entriesCount: entries_count,
    knockoutsCount: knockouts_count,
    guaranteePoints: tournament.guarantee_points || 5000,
    hasBounty: !!tournament.has_bounty,
    bountyPoints: tournament.bounty_points || 0,
    prizeDistribution: tournament.prize_distribution || undefined,
  };
  const calc = calculateRatingPoints(result);

  await supabase.from('tournament_results').insert({
    tournament_id: tournamentId, user_id, final_position, entries_count, knockouts_count,
    points_earned: calc.totalPoints, stage_reached: calc.stageReached,
  });

  // Skip rating updates for guest users
  const { data: userRecord } = await supabase.from('users').select('is_guest').eq('id', user_id).single();
  if (userRecord?.is_guest) {
    return { ...calc, xpEarned: 0, leveledUp: false, newLevel: 1, newStreak: 0 };
  }

  const { data: pr } = await supabase.from('player_ratings').select('*').eq('user_id', user_id).maybeSingle();
  const isWin = final_position === 1 ? 1 : 0;
  const isT3 = final_position <= 3 ? 1 : 0;
  const isT4 = final_position <= 4 ? 1 : 0;
  const isFT = final_position <= 9 ? 1 : 0;   // Топ-9
  const isT10 = final_position <= 18 ? 1 : 0;  // Топ-18

  if (pr) {
    const daysSinceLast = pr.last_tournament_at
      ? (Date.now() - new Date(pr.last_tournament_at).getTime()) / (1000 * 60 * 60 * 24)
      : null;
    const newStreak = daysSinceLast !== null && daysSinceLast <= 60 ? (pr.current_streak || 1) + 1 : 1;

    const xpEarned = calculateXP(final_position, total_players, knockouts_count, newStreak, !!tournament.has_bounty);
    const oldXP = pr.xp || 0;
    const newXP = oldXP + xpEarned;
    const oldLevel = calculateLevel(oldXP);
    const newLevel = calculateLevel(newXP);
    const leveledUp = newLevel > oldLevel;

    const newRating = updatePlayerRating(pr.current_rating, calc.totalPoints);
    const newTotalTournaments = pr.total_tournaments + 1;
    const coinsForTournament = 20 + Math.floor(calc.totalPoints / 20);

    const updatePayload = {
      total_tournaments: newTotalTournaments,
      total_points: pr.total_points + calc.totalPoints,
      current_rating: newRating,
      monthly_rating: pr.monthly_rating + calc.totalPoints,
      wins: pr.wins + isWin,
      final_tables: pr.final_tables + isFT,
      top_10_finishes: pr.top_10_finishes + isT10,
      top_4_finishes: pr.top_4_finishes + isT4,
      top_3_finishes: pr.top_3_finishes + isT3,
      total_knockouts: (pr.total_knockouts || 0) + knockouts_count,
      best_finish: (!pr.best_finish || pr.best_finish > final_position) ? final_position : pr.best_finish,
      xp: newXP,
      current_level: newLevel,
      current_streak: newStreak,
      last_tournament_at: moscowNow(),
      last_updated: moscowNow(),
      coins_balance: (pr.coins_balance || 0) + coinsForTournament,
    };
    let updated: any = null, updateError: any = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      const res = await supabase.from('player_ratings').update(updatePayload).eq('user_id', user_id).select().single();
      updated = res.data; updateError = res.error;
      if (!updateError) break;
      await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
    }
    if (updateError) throw updateError;
    // Grant win-unlocked skins at the moment the win is recorded, so they
    // arrive regardless of how the player logs in (Telegram vs email).
    if (isWin) {
      try { await _giveWinItems(user_id, pr.wins + isWin); } catch { /* non-critical */ }
    }
    return { ...calc, xpEarned, leveledUp, newLevel, newStreak };
  } else {
    const xpEarned = calculateXP(final_position, total_players, knockouts_count, 1, !!tournament.has_bounty);
    const newXP = xpEarned;
    const newLevel = calculateLevel(newXP);
    const newRating = updatePlayerRating(0, calc.totalPoints);
    const coinsForTournament = 20 + Math.floor(calc.totalPoints / 20);
    const insertPayload = {
      user_id, total_tournaments: 1, total_points: calc.totalPoints, current_rating: newRating,
      monthly_rating: calc.totalPoints, wins: isWin, final_tables: isFT,
      top_10_finishes: isT10, top_4_finishes: isT4, top_3_finishes: isT3,
      total_knockouts: knockouts_count,
      best_finish: final_position,
      xp: newXP,
      current_level: newLevel,
      current_streak: 1,
      last_tournament_at: moscowNow(),
      coins_balance: 5 + coinsForTournament,
    };
    let insertError: any = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      const res = await supabase.from('player_ratings').insert(insertPayload);
      insertError = res.error;
      if (!insertError) break;
      await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
    }
    if (insertError) throw insertError;
    // First-ever result: grant win-unlocked skins if this was a win.
    if (isWin) {
      try { await _giveWinItems(user_id, isWin); } catch { /* non-critical */ }
    }
    return { ...calc, xpEarned, leveledUp: newLevel > 1, newLevel, newStreak: 1 };
  }
}

export async function submitResultsBulk(
  tournamentId: number,
  results: Array<{ user_id: number; final_position: number; entries_count?: number; knockouts_count?: number; free_entries_earned?: number }>,
  total_players: number,
) {
  const { data: t } = await supabase.from('tournaments').select('*').eq('id', tournamentId).single();
  if (!t) throw new Error('Tournament not found');

  await supabase.from('tournaments').update({ total_players }).eq('id', tournamentId);

  const processed: any[] = [];
  const errors: any[] = [];
  const affected = new Set<number>();

  await Promise.all(results.map(async (r) => {
    try {
      const { user_id, final_position, entries_count = 1, knockouts_count = 0, free_entries_earned = 0 } = r;
      if (!user_id || !final_position) { errors.push({ result: r, error: 'Missing user_id or final_position' }); return; }
      const { data: existing } = await supabase.from('tournament_results')
        .select('id').eq('tournament_id', tournamentId).eq('user_id', user_id).maybeSingle();
      if (existing) {
        // Update existing result without re-awarding XP/rating
        const result: TournamentResult = {
          tournamentId, userId: user_id, finalPosition: final_position,
          totalPlayers: total_players, tournamentType: t.tournament_type as any,
          entriesCount: entries_count, knockoutsCount: knockouts_count,
          guaranteePoints: t.guarantee_points || 5000, hasBounty: !!t.has_bounty,
          bountyPoints: t.bounty_points || 0, prizeDistribution: t.prize_distribution || undefined,
        };
        const calc = calculateRatingPoints(result);
        await supabase.from('tournament_results').update({
          final_position, entries_count, knockouts_count,
          points_earned: calc.totalPoints, stage_reached: calc.stageReached,
        }).eq('id', existing.id);
        affected.add(user_id);
        processed.push({ user_id, final_position, points_earned: calc.totalPoints, updated: true });
        return;
      }
      const calc = await processResult(tournamentId, user_id, final_position, entries_count, knockouts_count, total_players, t);
      affected.add(user_id);
      processed.push({ user_id, final_position, points_earned: calc.totalPoints, xp_earned: calc.xpEarned, stage_reached: calc.stageReached });

      // Award free entry cards
      if (free_entries_earned > 0) {
        const { data: pr } = await supabase.from('player_ratings').select('free_entries').eq('user_id', user_id).single();
        await supabase.from('player_ratings').update({
          free_entries: (pr?.free_entries || 0) + free_entries_earned,
        }).eq('user_id', user_id);
        notify.notifyFreeEntries(user_id, free_entries_earned);
      }

      // Notify results
      const coinsEarned = 20 + Math.floor(calc.totalPoints / 20);
      notify.notifyResults(user_id, t.name, final_position, calc.totalPoints, coinsEarned);

      // Notify level up
      if (calc.leveledUp) {
        const lvlNames = ['Новичок','Ученик','Игрок','Опытный','Мастер','Эксперт','Профи','Легенда','Икона','Миф'];
        notify.notifyLevelUp(user_id, calc.newLevel, lvlNames[Math.min(Math.floor((calc.newLevel - 1) / 10), 9)] || '');
      }
    } catch (e: any) { errors.push({ result: r, error: e.message }); }
  }));

  // ── Award participation to checked-in players without results ──
  const submittedUserIds = results.map(r => r.user_id).filter(Boolean);
  const { data: checkedIn } = await supabase.from('tournament_registrations')
    .select('user_id').eq('tournament_id', tournamentId).eq('checked_in', true);
  const checkedInIds = (checkedIn || []).map((r: any) => r.user_id);
  const missingIds = checkedInIds.filter((uid: number) => !submittedUserIds.includes(uid));

  let participationCount = 0;
  for (const uid of missingIds) {
    try {
      // Check if already has a result
      const { data: existing } = await supabase.from('tournament_results')
        .select('id').eq('tournament_id', tournamentId).eq('user_id', uid).maybeSingle();
      if (existing) continue;

      // Skip guests
      const { data: userRecord } = await supabase.from('users').select('is_guest').eq('id', uid).single();
      if (userRecord?.is_guest) continue;

      // Give them last place position for XP calculation
      const participationPosition = total_players;
      await supabase.from('tournament_results').insert({
        tournament_id: tournamentId, user_id: uid, final_position: participationPosition,
        entries_count: 1, knockouts_count: 0, points_earned: 0, stage_reached: 'early',
      });

      // Update player_ratings: +1 tournament, +XP, +coins
      const { data: pr } = await supabase.from('player_ratings').select('*').eq('user_id', uid).maybeSingle();
      const streak = pr?.last_tournament_at
        ? ((Date.now() - new Date(pr.last_tournament_at).getTime()) / (1000*60*60*24) <= 60 ? (pr?.current_streak || 1) + 1 : 1)
        : 1;
      const xpEarned = calculateXP(participationPosition, total_players, 0, streak, !!t.has_bounty);
      const coinsEarned = 20;

      if (pr) {
        const newXP = (pr.xp || 0) + xpEarned;
        await supabase.from('player_ratings').update({
          total_tournaments: (pr.total_tournaments || 0) + 1,
          xp: newXP,
          current_level: calculateLevel(newXP),
          current_streak: streak,
          last_tournament_at: moscowNow(),
          last_updated: moscowNow(),
          coins_balance: (pr.coins_balance || 0) + coinsEarned,
        }).eq('user_id', uid);
      } else {
        await supabase.from('player_ratings').insert({
          user_id: uid, total_tournaments: 1, total_points: 0, current_rating: 0,
          monthly_rating: 0, xp: xpEarned, current_level: calculateLevel(xpEarned),
          current_streak: 1, last_tournament_at: moscowNow(), coins_balance: 5 + coinsEarned,
        });
      }
      participationCount++;
      affected.add(uid);
    } catch { /* skip individual failures */ }
  }

  // Recompute canonical stats for everyone touched, so placement counters,
  // best_finish and the check-in-based tournament count stay correct and don't
  // drift from the incremental reward updates above.
  for (const uid of affected) {
    try { await recomputeAggregatesFromResults(uid); } catch { /* non-critical */ }
  }

  return { message: `Processed ${processed.length} results, ${participationCount} participation credits`, processed: processed.length, participation: participationCount, errors: errors.length, details: errors.length ? errors : undefined };
}

export async function getTournamentResults(tournamentId: number) {
  const { data, error } = await supabase.from('tournament_results')
    .select('*, users(first_name, last_name, username, display_name, is_guest, invited_by)')
    .eq('tournament_id', tournamentId).order('final_position', { ascending: true });
  if (error) throw error;
  return (data || []).map((r: any) => ({ ...r, first_name: r.users?.first_name, last_name: r.users?.last_name, username: r.users?.username, display_name: r.users?.display_name, is_guest: r.users?.is_guest, invited_by: r.users?.invited_by, users: undefined }));
}

export async function getUserTournamentHistory(userId: number, limit = 50) {
  const { data, error } = await supabase.from('tournament_results')
    .select('*, tournaments(name, tournament_type, start_date, total_players)')
    .eq('user_id', userId).order('created_at', { ascending: false }).limit(limit);
  if (error) throw error;
  return (data || []).map((r: any) => ({
    ...r,
    tournament_name: r.tournaments?.name,
    tournament_type: r.tournaments?.tournament_type,
    start_date: r.tournaments?.start_date,
    total_players: r.tournaments?.total_players,
    tournaments: undefined,
  }));
}

export async function resetMonthlyRatings() {
  const { error } = await supabase.from('player_ratings').update({ monthly_rating: 0 }).gte('id', 0);
  if (error) throw error;
}

/* ═══════════════════════════════════════════════════════
   ORDERS
   ═══════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════
   HUCOIN — internal comp currency (admin-granted, admin-spent)
   All classes settle 1 HUcoin = 1 ₽. Coverage caps differ:
     • game  (re-entry/buy-in/add-ons) — up to 100% of the order
     • bar   (food/drinks)             — up to 50% of the order
     • hookah                          — not payable with HUcoin
   Coins are universal; an order can be settled partially (part HUcoin,
   part cash). See supabase-hucoin.sql / supabase-hucoin-v2.sql.
   ═══════════════════════════════════════════════════════ */

export type PayClass = 'game' | 'bar' | 'hookah';

// Per-class rules. rate = coins per ruble; maxPct = share of the order total
// coins may cover across its lifetime; payable = coins accepted at all.
export const HUCOIN_RULES: Record<PayClass, { rate: number; maxPct: number; payable: boolean }> = {
  game:   { rate: 1, maxPct: 1,   payable: true },
  bar:    { rate: 1, maxPct: 0.5, payable: true },
  hookah: { rate: 1, maxPct: 0,   payable: false },
};
// Back-compat: rate map referenced by order creation (all 1:1 now).
export const HUCOIN_RATES = { game: 1, bar: 1 } as const;

// Payment class of an order. Prefer the frozen pay_class; fall back to the
// legacy frozen rate (1 = game, 4 = bar), then infer from item names.
export function hucoinClassForOrder(o: { pay_class?: string | null; hucoin_rate?: number | null; items?: any }): PayClass {
  if (o.pay_class === 'game' || o.pay_class === 'bar' || o.pay_class === 'hookah') return o.pay_class;
  if (o.hucoin_rate === 4) return 'bar';
  if (o.hucoin_rate === 1) return 'game';
  const items = Array.isArray(o.items) ? o.items : safeParseJSON(o.items);
  const isGame = (items || []).some((it: any) => /ре-?ентри|стартовый стек|аддон|add-?on/i.test(it?.name || ''));
  return isGame ? 'game' : 'bar';
}
export function hucoinRuleForOrder(o: any) { return HUCOIN_RULES[hucoinClassForOrder(o)]; }
// Coins-per-ruble (kept for callers/reporting) — always 1 under the current rules.
export function hucoinRateForOrder(o: any): number { return hucoinRuleForOrder(o).rate; }

// Rubles of this order still owed (not covered by HUcoin, not yet cash-settled).
export function orderRemainingRub(o: any): number {
  if (o?.paid) return 0;
  return Math.max(0, (o?.total_price || 0) - (o?.hucoin_rub || 0));
}
// Total rubles of this order coins may EVER cover (class cap × total).
export function orderHucoinCapRub(o: any): number {
  const rule = hucoinRuleForOrder(o);
  if (!rule.payable) return 0;
  return Math.floor((o?.total_price || 0) * rule.maxPct);
}
// Rubles coins may cover RIGHT NOW (cap minus what's already covered, bounded by remainder).
export function orderHucoinCoverableRub(o: any): number {
  if (o?.paid) return 0;
  const already = o?.hucoin_rub || 0;
  const remaining = Math.max(0, (o?.total_price || 0) - already);
  return Math.max(0, Math.min(remaining, orderHucoinCapRub(o) - already));
}
// For reporting: split what was actually collected into cash vs HUcoin (both in ₽).
// Coins are collected whenever hucoin_rub > 0 (they were already spent); the cash
// part is only collected once the order is marked paid.
export function orderCoinRub(o: any): number { return o?.hucoin_rub || 0; }
export function orderCashRub(o: any): number {
  return o?.paid ? Math.max(0, (o?.total_price || 0) - (o?.hucoin_rub || 0)) : 0;
}

export async function getHucoinBalance(userId: number): Promise<number> {
  const { data } = await supabase.from('player_ratings').select('hucoin_balance').eq('user_id', userId).maybeSingle();
  return data?.hucoin_balance || 0;
}

export async function getHucoinLog(userId: number, limit = 100) {
  const { data, error } = await supabase.from('hucoin_log')
    .select('*').eq('user_id', userId).order('id', { ascending: false }).limit(limit);
  if (error) throw error;
  return data || [];
}

// Admin: recent HUcoin operations across all players (with player names).
export async function getRecentHucoinLog(limit = 60) {
  const { data, error } = await supabase.from('hucoin_log')
    .select('*, users(display_name, first_name, last_name, username)')
    .order('id', { ascending: false }).limit(limit);
  if (error) throw error;
  return (data || []).map((r: any) => ({
    ...r,
    name: r.users?.display_name || `${r.users?.first_name || ''} ${r.users?.last_name || ''}`.trim() || r.users?.username || `#${r.user_id}`,
    users: undefined,
  }));
}

// Admin: grant (positive) or correct (negative) a player's HUcoin balance.
export async function grantHucoin(userId: number, amount: number, note?: string, adminId?: number) {
  if (!Number.isInteger(amount) || amount === 0) throw new Error('Введите ненулевую сумму');
  await ensurePlayerRating(userId);
  const { data: pr } = await supabase.from('player_ratings').select('hucoin_balance').eq('user_id', userId).single();
  const newBal = (pr?.hucoin_balance || 0) + amount;
  if (newBal < 0) throw new Error('Баланс не может стать отрицательным');
  const { error } = await supabase.from('player_ratings').update({ hucoin_balance: newBal }).eq('user_id', userId);
  if (error) throw error;
  await supabase.from('hucoin_log').insert({
    user_id: userId, delta: amount, balance_after: newBal,
    reason: note?.trim() || (amount > 0 ? 'Начисление' : 'Списание'), admin_id: adminId || null,
  });
  return newBal;
}

// Admin: cover `rubToCover` rubles of an order with HUcoin ('all' = whole remainder).
// Spends rubToCover × rate coins; any ruble remainder stays a normal debt.
export async function payOrderWithHucoin(orderId: number, rubToCover: number | 'all', adminId?: number) {
  const { data: order } = await supabase.from('orders').select('*').eq('id', orderId).single();
  if (!order) throw new Error('Заказ не найден');
  if (order.paid) throw new Error('Заказ уже оплачен');
  if (order.status === 'cancelled') throw new Error('Заказ отменён');
  const parsed = { ...order, items: safeParseJSON(order.items) };
  const cls = hucoinClassForOrder(parsed);
  const rule = HUCOIN_RULES[cls];
  if (!rule.payable) throw new Error('Этот заказ нельзя оплатить HUcoin');
  const rate = rule.rate;
  const coverable = orderHucoinCoverableRub(parsed);
  const coverRub = rubToCover === 'all' ? coverable : Math.min(Math.max(0, Math.floor(rubToCover)), coverable);
  if (coverRub <= 0) {
    const cap = orderHucoinCapRub(parsed);
    throw new Error(cls === 'bar' ? `Баром монетами можно закрыть не больше ${cap} ₽ (50%)` : 'Нечего погашать монетами');
  }
  const coins = coverRub * rate;

  const { data: pr } = await supabase.from('player_ratings').select('hucoin_balance').eq('user_id', order.user_id).single();
  const bal = pr?.hucoin_balance || 0;
  if (bal < coins) throw new Error(`Недостаточно HUcoin: нужно ${coins}, есть ${bal}`);
  const newBal = bal - coins;
  const newRub = (order.hucoin_rub || 0) + coverRub;
  const fully = newRub >= (order.total_price || 0);

  const { error: balErr } = await supabase.from('player_ratings').update({ hucoin_balance: newBal }).eq('user_id', order.user_id);
  if (balErr) throw balErr;
  const { error: ordErr } = await supabase.from('orders').update({
    hucoin_rub: newRub, hucoin_rate: rate, pay_class: cls,
    paid: fully ? true : order.paid,
    paid_with: fully ? (newRub > coverRub ? 'mixed' : 'hucoin') : (order.paid_with || null),
  }).eq('id', orderId);
  if (ordErr) {
    await supabase.from('player_ratings').update({ hucoin_balance: bal }).eq('user_id', order.user_id); // refund
    throw ordErr;
  }
  const summary = (parsed.items || []).map((it: any) => it.name || `#${it.menu_item_id}`).join(', ');
  await supabase.from('hucoin_log').insert({
    user_id: order.user_id, delta: -coins, balance_after: newBal,
    reason: `Оплата ${coverRub} ₽${summary ? ` · ${summary}` : ''}`.slice(0, 200),
    order_id: orderId, admin_id: adminId || null,
  });
  return { coins, coverRub, newBal, fully };
}

// Admin: settle ALL of a player's outstanding ruble debt with HUcoin at once.
export async function payAllWithHucoin(userId: number, adminId?: number) {
  const { data } = await supabase.from('orders').select('*')
    .eq('user_id', userId).eq('paid', false).neq('status', 'cancelled').or('free.is.null,free.eq.false')
    .order('id', { ascending: true });
  const orders = (data || []).map((o: any) => ({ ...o, items: safeParseJSON(o.items) }));
  const totalCoins = orders.reduce((s: number, o: any) => s + orderHucoinCoverableRub(o) * hucoinRuleForOrder(o).rate, 0);
  const bal = await getHucoinBalance(userId);
  if (totalCoins <= 0) throw new Error('Нет заказов, которые можно оплатить HUcoin');
  if (bal < totalCoins) throw new Error(`Недостаточно HUcoin: нужно ${totalCoins}, есть ${bal}`);
  let settled = 0;
  for (const o of orders) { if (orderHucoinCoverableRub(o) > 0) { await payOrderWithHucoin(o.id, 'all', adminId); settled++; } }
  return { settled, totalCoins };
}

export async function createOrder(data: {
  user_id: number; items: Array<{ menu_item_id: number; quantity: number }>;
  table_number?: string; tournament_id?: number;
}) {
  const menuItemsToUpdate: Array<{ id: number; quantity: number }> = [];

  const menuItems = await Promise.all(data.items.map(async (item) => {
    const { data: menuItem } = await supabase.from('menu_items')
      .select('*').eq('id', item.menu_item_id).eq('available', true).single();
    if (!menuItem) throw new Error(`Item ${item.menu_item_id} not found or unavailable`);
    return { menuItem, orderQuantity: item.quantity };
  }));

  // Stock check + partition items by HUcoin pay class so each order carries a
  // single class/rate (game add-ons, bar, non-payable hookah).
  const groups: Record<PayClass, { items: Array<{ menu_item_id: number; quantity: number }>; total: number }> = {
    game: { items: [], total: 0 }, bar: { items: [], total: 0 }, hookah: { items: [], total: 0 },
  };
  for (const { menuItem, orderQuantity } of menuItems) {
    if (!menuItem.unlimited) {
      const currentQty = menuItem.quantity || 0;
      if (currentQty < orderQuantity) throw new Error(`Not enough "${menuItem.name}". Available: ${currentQty}`);
      menuItemsToUpdate.push({ id: menuItem.id, quantity: orderQuantity });
    }
    const cls: PayClass = menuItem.pay_class === 'game' ? 'game' : menuItem.pay_class === 'hookah' ? 'hookah' : 'bar';
    groups[cls].items.push({ menu_item_id: menuItem.id, quantity: orderQuantity });
    groups[cls].total += menuItem.price * orderQuantity;
  }

  const created: any[] = [];
  for (const cls of ['game', 'bar', 'hookah'] as const) {
    const g = groups[cls];
    if (g.items.length === 0) continue;
    const { data: order, error } = await supabase.from('orders').insert({
      user_id: data.user_id, items: JSON.stringify(g.items), total_price: g.total,
      table_number: data.table_number || null,
      tournament_id: data.tournament_id || null, status: 'pending',
      hucoin_rate: HUCOIN_RULES[cls].rate, pay_class: cls,
    }).select().single();
    if (error) throw error;
    created.push(order);
  }

  await Promise.all(menuItemsToUpdate.map(async (u) => {
    const { data: curr } = await supabase.from('menu_items').select('quantity').eq('id', u.id).single();
    if (curr) {
      await supabase.from('menu_items').update({ quantity: Math.max(0, (curr.quantity || 0) - u.quantity) }).eq('id', u.id);
    }
  }));

  return created.length === 1 ? created[0] : created;
}

export async function getOrdersByUser(userId: number) {
  const { data, error } = await supabase.from('orders')
    .select('*, tournaments(name)')
    .eq('user_id', userId).order('created_at', { ascending: false }).limit(50);
  if (error) throw error;
  return (data || []).map((o: any) => ({
    ...o,
    items: safeParseJSON(o.items),
    tournament_name: o.tournaments?.name || null,
    tournaments: undefined,
  }));
}

export async function getOrdersByTournament(tournamentId: number) {
  const { data, error } = await supabase
    .from('orders')
    .select('*, users(first_name, last_name, username, display_name)')
    .eq('tournament_id', tournamentId)
    .neq('status', 'cancelled')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((o: any) => ({
    ...o,
    first_name: o.users?.first_name, last_name: o.users?.last_name, username: o.users?.username, display_name: o.users?.display_name,
    users: undefined,
    items: safeParseJSON(o.items),
  }));
}

export async function getAllOrders(status?: string, tournamentId?: number) {
  let query = supabase.from('orders').select('*, users(first_name, last_name, username, display_name), tournaments(name)').order('created_at', { ascending: false });
  if (status) query = query.eq('status', status);
  if (tournamentId) query = query.eq('tournament_id', tournamentId);

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map((o: any) => ({
    ...o,
    first_name: o.users?.first_name, last_name: o.users?.last_name, username: o.users?.username, display_name: o.users?.display_name,
    tournament_name: o.tournaments?.name || null,
    users: undefined, tournaments: undefined,
    items: safeParseJSON(o.items),
  }));
}

export async function getOrdersCount(tournamentId?: number): Promise<number> {
  let query = supabase.from('orders').select('id', { count: 'exact', head: true });
  if (tournamentId) query = query.eq('tournament_id', tournamentId);
  const { count, error } = await query;
  if (error) throw error;
  return count ?? 0;
}

export async function updateOrderStatus(orderId: number, status: string) {
  const { error } = await supabase.from('orders').update({ status }).eq('id', orderId);
  if (error) throw error;

  if (status === 'ready') {
    try {
      const { data: order } = await supabase.from('orders').select('user_id, items').eq('id', orderId).single();
      if (order) {
        const items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
        const names = items.map((i: any) => i.name || `Товар #${i.menu_item_id}`).join(', ');
        notify.notifyOrderReady(order.user_id, names);
      }
    } catch {}
  }
}

export async function getUnpaidOrders(userId: number) {
  const { data, error } = await supabase.from('orders').select('*')
    .eq('user_id', userId).eq('paid', false).neq('status', 'cancelled')
    .or('free.is.null,free.eq.false')
    .order('created_at', { ascending: false });
  if (error) throw error;
  const orders = (data || []).map((o: any) => ({ ...o, items: safeParseJSON(o.items) }));
  const total = orders.reduce((sum: number, o: any) => sum + Math.max(0, (o.total_price || 0) - (o.hucoin_rub || 0)), 0);
  return { orders, total, count: orders.length };
}

export async function markOrderPaid(orderId: number, paid: boolean, free?: boolean) {
  const { error } = await supabase.from('orders').update({ paid, free: free || false }).eq('id', orderId);
  if (error) throw error;
}

export async function payUserOrders(userId: number) {
  const { data, error } = await supabase.from('orders')
    .update({ paid: true })
    .eq('user_id', userId).eq('paid', false).neq('status', 'cancelled')
    .select();
  if (error) throw error;
  return { success: true, paid_count: data?.length || 0, message: 'Оплата прошла успешно!' };
}

// Admin debts view: one row per player with outstanding (unpaid, non-free,
// non-cancelled) orders — their order count and total owed.
export async function getDebtors() {
  // Paginate: debt-eligible orders can exceed the 1000-row PostgREST cap, which
  // would silently drop rows and undercount some players' badge totals.
  const data = await fetchAllPaginated<any>(() => supabase.from('orders')
    .select('user_id, total_price, hucoin_rub, users!inner(display_name, first_name, last_name, username, is_guest)')
    .eq('paid', false).neq('status', 'cancelled').or('free.is.null,free.eq.false')
    .order('id', { ascending: true }));
  const byUser = new Map<number, any>();
  for (const o of (data as any[]) || []) {
    // Ruble debt is what's left after any partial HUcoin coverage.
    const remaining = Math.max(0, (o.total_price || 0) - (o.hucoin_rub || 0));
    if (remaining <= 0) continue;
    let u = byUser.get(o.user_id);
    if (!u) {
      u = {
        user_id: o.user_id,
        name: o.users?.display_name || `${o.users?.first_name || ''} ${o.users?.last_name || ''}`.trim() || o.users?.username || `#${o.user_id}`,
        username: o.users?.username || null,
        is_guest: !!o.users?.is_guest,
        count: 0,
        total: 0,
      };
      byUser.set(o.user_id, u);
    }
    u.count += 1;
    u.total += remaining;
  }
  return Array.from(byUser.values())
    .filter(u => u.total > 0)
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

// A debtor's outstanding orders, with the tournament each belongs to.
export async function getUserDebts(userId: number) {
  const { data, error } = await supabase.from('orders')
    .select('id, user_id, items, total_price, status, paid, free, created_at, tournament_id, hucoin_rate, hucoin_rub, pay_class, tournaments(name)')
    .eq('user_id', userId).eq('paid', false).neq('status', 'cancelled').or('free.is.null,free.eq.false')
    .order('created_at', { ascending: false });
  if (error) throw error;
  const orders = (data || []).map((o: any) => ({
    id: o.id,
    user_id: o.user_id,
    total_price: o.total_price,
    status: o.status,
    paid: !!o.paid,
    free: !!o.free,
    created_at: o.created_at,
    tournament_name: o.tournaments?.name || null,
    hucoin_rate: o.hucoin_rate ?? null,
    hucoin_rub: o.hucoin_rub || 0,
    pay_class: o.pay_class ?? null,
    items: safeParseJSON(o.items),
  }));

  // Menu orders store items as { menu_item_id, quantity } with no name — resolve
  // the names from menu_items so the breakdown doesn't show "undefined".
  const ids = new Set<number>();
  for (const o of orders) for (const it of (o.items || [])) {
    if (it && !it.name && it.menu_item_id != null) ids.add(it.menu_item_id);
  }
  if (ids.size) {
    const { data: mi } = await supabase.from('menu_items').select('id, name').in('id', Array.from(ids));
    const nameById = new Map((mi || []).map((m: any) => [m.id, m.name]));
    for (const o of orders) {
      o.items = (o.items || []).map((it: any) => ({
        ...it,
        name: it?.name || nameById.get(it?.menu_item_id) || (it?.menu_item_id != null ? `Товар #${it.menu_item_id}` : 'Товар'),
      }));
    }
  }
  return orders;
}

/* ═══════════════════════════════════════════════════════
   MENU
   ═══════════════════════════════════════════════════════ */

export async function getMenuItems(category?: string) {
  let query = supabase.from('menu_items').select('*').eq('available', true).order('category').order('name');
  if (category) query = query.eq('category', category);
  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function getAllMenuItems() {
  const { data, error } = await supabase.from('menu_items').select('*').order('category').order('name');
  if (error) throw error;
  return data;
}

export async function createMenuItem(data: {
  name: string; description?: string | null; price: number;
  category: string; image_url?: string | null; quantity?: number | null; unlimited?: boolean; pay_class?: string;
}) {
  if (!data.name || data.name.trim().length === 0) throw new Error('Название позиции обязательно');
  if (data.price < 0) throw new Error('Цена не может быть отрицательной');
  if (!data.category) throw new Error('Категория обязательна');

  const { data: item, error } = await supabase.from('menu_items').insert({
    name: data.name.trim(), description: data.description || null, price: data.price,
    category: data.category, image_url: data.image_url || null,
    quantity: data.unlimited ? null : (data.quantity || null), unlimited: !!data.unlimited,
    pay_class: (data.pay_class === 'game' || data.pay_class === 'hookah') ? data.pay_class : 'bar',
  }).select().single();
  if (error) throw error;
  return item;
}

export async function updateMenuItem(id: number, data: {
  name: string; description?: string | null; price: number;
  category: string; image_url?: string | null; quantity?: number | null; unlimited?: boolean; available?: boolean; pay_class?: string;
}) {
  const { data: item, error } = await supabase.from('menu_items').update({
    name: data.name, description: data.description || null, price: data.price,
    category: data.category, image_url: data.image_url || null,
    quantity: data.unlimited ? null : (data.quantity || null), unlimited: !!data.unlimited,
    available: data.available !== undefined ? data.available : true,
    pay_class: (data.pay_class === 'game' || data.pay_class === 'hookah') ? data.pay_class : 'bar',
  }).eq('id', id).select().single();
  if (error) throw error;
  return item;
}

export async function deleteMenuItem(id: number) {
  const { error } = await supabase.from('menu_items').delete().eq('id', id);
  if (error) throw error;
}

/* ═══════════════════════════════════════════════════════
   RATINGS
   ═══════════════════════════════════════════════════════ */

export async function getUserRating(userId: number) {
  const { data, error } = await supabase.from('player_ratings')
    .select('*, users(first_name, last_name, username, display_name)')
    .eq('user_id', userId).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return { ...data, first_name: data.users?.first_name, last_name: data.users?.last_name, username: data.users?.username, display_name: data.users?.display_name, users: undefined };
}

export async function getTopRatings(limit = 100) {
  const { data, error } = await supabase.from('player_ratings')
    .select('*, users!inner(first_name, last_name, username, display_name, is_guest)')
    .eq('users.is_guest', false)
    .order('current_rating', { ascending: false })
    .order('id', { ascending: true })
    .limit(limit);
  if (error) throw error;
  return (data || []).map((r: any) => ({ ...r, first_name: r.users?.first_name, last_name: r.users?.last_name, username: r.users?.username, display_name: r.users?.display_name, users: undefined }));
}

export async function getKnockoutRatings(limit = 100) {
  const { data, error } = await supabase.from('player_ratings')
    .select('*, users!inner(first_name, last_name, username, display_name, is_guest)')
    .eq('users.is_guest', false)
    .gt('total_knockouts', 0)
    .order('total_knockouts', { ascending: false })
    .order('id', { ascending: true })
    .limit(limit);
  if (error) throw error;
  return (data || []).map((r: any) => ({ ...r, first_name: r.users?.first_name, last_name: r.users?.last_name, username: r.users?.username, display_name: r.users?.display_name, users: undefined }));
}

export async function getMonthlyRatings(limit = 100) {
  const { data, error } = await supabase.from('player_ratings')
    .select('*, users!inner(first_name, last_name, username, display_name, is_guest)')
    .eq('users.is_guest', false)
    .order('monthly_rating', { ascending: false })
    .order('id', { ascending: true })
    .limit(limit);
  if (error) throw error;
  return (data || []).map((r: any) => ({ ...r, first_name: r.users?.first_name, last_name: r.users?.last_name, username: r.users?.username, display_name: r.users?.display_name, users: undefined }));
}

export async function getMonthlyRatingsByMonth(year: number, month: number, limit = 100) {
  // Calculate ratings from tournament_results for a specific month
  const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
  const endDate = month === 12
    ? `${year + 1}-01-01`
    : `${year}-${String(month + 1).padStart(2, '0')}-01`;

  // Get all tournament results for tournaments in this month. Paginate to avoid
  // the 1000-row PostgREST cap silently dropping results in busy months.
  const results = await fetchAllPaginated<any>(() => supabase
    .from('tournament_results')
    .select('user_id, points_earned, tournaments!inner(start_date)')
    .gte('tournaments.start_date', startDate)
    .lt('tournaments.start_date', endDate)
    .order('id', { ascending: true }));

  // Aggregate by user
  const userPoints: Record<number, number> = {};
  for (const r of results || []) {
    userPoints[r.user_id] = (userPoints[r.user_id] || 0) + (r.points_earned || 0);
  }

  // Get user info for all users
  const userIds = Object.keys(userPoints).map(Number);
  if (userIds.length === 0) return [];

  const { data: users } = await supabase
    .from('users')
    .select('id, first_name, last_name, display_name, is_guest')
    .in('id', userIds);

  const usersMap = new Map((users || []).map(u => [u.id, u]));

  // Build sorted list
  return Object.entries(userPoints)
    .map(([uid, pts]) => {
      const u = usersMap.get(Number(uid));
      if (!u || u.is_guest) return null;
      return {
        user_id: Number(uid),
        monthly_rating: pts,
        current_rating: 0,
        first_name: u.first_name,
        last_name: u.last_name,
        display_name: u.display_name,
      };
    })
    .filter(Boolean)
    .sort((a: any, b: any) => b.monthly_rating - a.monthly_rating)
    .slice(0, limit);
}

export async function getAvailableMonths(): Promise<string[]> {
  // Get distinct months from tournaments that have results
  const { data, error } = await supabase
    .from('tournaments')
    .select('start_date')
    .not('start_date', 'is', null)
    .order('start_date', { ascending: false });
  if (error) throw error;

  const months = new Set<string>();
  for (const t of data || []) {
    const d = new Date(t.start_date);
    months.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return Array.from(months).sort().reverse();
}

// Series-based ratings: aggregates stats from tournament_results for an
// arbitrary date range. Used for "series" tabs that don't align with
// calendar months (e.g. a series may start mid-month).
//
// Stats (points, tournament count, wins, knockouts) are computed FOR THE
// SERIES — not all-time — so the ranking tab is internally consistent.
interface SeriesUserStats {
  points: number;
  tournaments: Set<number>;
  wins: number;
  knockouts: number;
}

async function _aggregateSeriesStats(
  startDate: string,
  endDateExclusive: string,
  mptOnly = false
): Promise<Map<number, SeriesUserStats>> {
  // Paginate: a busy series can exceed the 1000-row PostgREST cap, which would
  // silently drop results and undercount players' points.
  const results = await fetchAllPaginated<any>(() => {
    let q = supabase
      .from('tournament_results')
      .select('user_id, points_earned, final_position, knockouts_count, tournament_id, tournaments!inner(start_date, is_mpt)')
      .gte('tournaments.start_date', startDate)
      .lt('tournaments.start_date', endDateExclusive);
    if (mptOnly) q = q.eq('tournaments.is_mpt', true);
    return q.order('id', { ascending: true });
  });

  return _reduceSeriesStats(results);
}

// Shared reducer: tournament_results rows → per-user series stats.
function _reduceSeriesStats(results: any[]): Map<number, SeriesUserStats> {
  const stats = new Map<number, SeriesUserStats>();
  for (const r of results || []) {
    let s = stats.get(r.user_id);
    if (!s) {
      s = { points: 0, tournaments: new Set(), wins: 0, knockouts: 0 };
      stats.set(r.user_id, s);
    }
    s.points += r.points_earned || 0;
    if (r.tournament_id != null) s.tournaments.add(r.tournament_id);
    if (r.final_position === 1) s.wins += 1;
    s.knockouts += r.knockouts_count || 0;
  }
  return stats;
}

// TV-стол series: aggregate results for all tournaments tied to one tv_series_id
// (membership is manual, not date-based). Paginated for the 1000-row cap.
async function _aggregateTvSeriesStats(tvSeriesId: number): Promise<Map<number, SeriesUserStats>> {
  const results = await fetchAllPaginated<any>(() => supabase
    .from('tournament_results')
    .select('user_id, points_earned, final_position, knockouts_count, tournament_id, tournaments!inner(tv_series_id)')
    .eq('tournaments.tv_series_id', tvSeriesId)
    .order('id', { ascending: true }));
  return _reduceSeriesStats(results);
}

// Shared: series stats map → sorted leaderboard rows (non-guests only).
async function _seriesRatingsFromStats(userStats: Map<number, SeriesUserStats>, limit: number) {
  const userIds = Array.from(userStats.keys());
  if (userIds.length === 0) return [];
  const [{ data: users }, { data: prRows }] = await Promise.all([
    supabase.from('users').select('id, first_name, last_name, display_name, is_guest').in('id', userIds),
    supabase.from('player_ratings').select('user_id, equipped_char').in('user_id', userIds),
  ]);
  const usersMap = new Map((users || []).map(u => [u.id, u]));
  const equippedMap = new Map((prRows || []).map((r: any) => [r.user_id, r.equipped_char]));
  return Array.from(userStats.entries())
    .map(([uid, s]) => {
      const u = usersMap.get(uid);
      if (!u || u.is_guest) return null;
      return {
        user_id: uid,
        monthly_rating: s.points,
        current_rating: 0,
        first_name: u.first_name,
        last_name: u.last_name,
        display_name: u.display_name,
        wins: s.wins,
        total_tournaments: s.tournaments.size,
        total_knockouts: s.knockouts,
        equipped_char: equippedMap.get(uid) || null,
      };
    })
    .filter(Boolean)
    .sort((a: any, b: any) => b.monthly_rating - a.monthly_rating || a.user_id - b.user_id)
    .slice(0, limit);
}

// Shared: a user's position within a series stats map.
async function _seriesPositionFromStats(userStats: Map<number, SeriesUserStats>, userId: number) {
  const mine = userStats.get(userId);
  if (!mine) throw new Error('User has no results in this series');
  const userIds = Array.from(userStats.keys());
  const { data: nonGuests } = await supabase.from('users').select('id').eq('is_guest', false).in('id', userIds);
  const validIds = new Set((nonGuests || []).map(u => u.id));
  if (!validIds.has(userId)) throw new Error('User excluded from series ranking');
  const ranked = Array.from(userStats.entries())
    .filter(([uid]) => validIds.has(uid))
    .sort(([a_id, a], [b_id, b]) => b.points - a.points || a_id - b_id);
  return {
    position: ranked.findIndex(([uid]) => uid === userId) + 1,
    rating: mine.points,
    wins: mine.wins,
    total_tournaments: mine.tournaments.size,
    total_knockouts: mine.knockouts,
    type: 'monthly' as const,
  };
}

/* ─── TV-стол series CRUD ─── */
export async function getTvSeries(): Promise<Array<{ id: number; name: string }>> {
  const { data, error } = await supabase.from('tv_series').select('id, name').order('id', { ascending: false });
  if (error) throw error;
  return data || [];
}
export async function createTvSeries(name: string): Promise<{ id: number; name: string }> {
  const clean = name.trim();
  if (!clean) throw new Error('Введите название серии');
  const { data, error } = await supabase.from('tv_series').insert({ name: clean }).select('id, name').single();
  if (error) throw error;
  return data;
}
export async function getTvSeriesRatings(tvSeriesId: number, limit = 100) {
  return _seriesRatingsFromStats(await _aggregateTvSeriesStats(tvSeriesId), limit);
}
export async function getUserTvSeriesPosition(userId: number, tvSeriesId: number) {
  return _seriesPositionFromStats(await _aggregateTvSeriesStats(tvSeriesId), userId);
}

export async function getSeriesRatings(startDate: string, endDateExclusive: string, limit = 100, mptOnly = false) {
  return _seriesRatingsFromStats(await _aggregateSeriesStats(startDate, endDateExclusive, mptOnly), limit);
}

export async function getUserSeriesPosition(
  userId: number,
  startDate: string,
  endDateExclusive: string,
  mptOnly = false
) {
  const userStats = await _aggregateSeriesStats(startDate, endDateExclusive, mptOnly);
  const mine = userStats.get(userId);
  if (!mine) {
    throw new Error('User has no results in this series');
  }

  const userIds = Array.from(userStats.keys());
  const { data: nonGuests } = await supabase
    .from('users')
    .select('id')
    .eq('is_guest', false)
    .in('id', userIds);
  const validIds = new Set((nonGuests || []).map(u => u.id));
  if (!validIds.has(userId)) {
    throw new Error('User excluded from series ranking');
  }

  const ranked = Array.from(userStats.entries())
    .filter(([uid]) => validIds.has(uid))
    .sort(([a_id, a], [b_id, b]) => b.points - a.points || a_id - b_id);

  return {
    position: ranked.findIndex(([uid]) => uid === userId) + 1,
    rating: mine.points,
    wins: mine.wins,
    total_tournaments: mine.tournaments.size,
    total_knockouts: mine.knockouts,
    type: 'monthly' as const,
  };
}

export async function getUserRatingPosition(userId: number, type: 'overall' | 'monthly' = 'overall') {
  const field = type === 'monthly' ? 'monthly_rating' : 'current_rating';
  const { data: userRating } = await supabase.from('player_ratings').select(`${field}, id`).eq('user_id', userId).single();
  if (!userRating) throw new Error('User not found in ratings');

  const ratingValue = (userRating as any)[field];
  const odId = (userRating as any).id;

  // Exclude guest/merged accounts from position calculation
  const [{ count: higherCount }, { count: sameButEarlier }] = await Promise.all([
    supabase.from('player_ratings').select('*, users!inner(is_guest)', { count: 'exact', head: true }).eq('users.is_guest', false).gt(field, ratingValue),
    supabase.from('player_ratings').select('*, users!inner(is_guest)', { count: 'exact', head: true }).eq('users.is_guest', false).eq(field, ratingValue).lt('id', odId),
  ]);

  return { position: (higherCount || 0) + (sameButEarlier || 0) + 1, rating: ratingValue, type };
}

/* ═══════════════════════════════════════════════════════
   BOOKINGS
   ═══════════════════════════════════════════════════════ */

export async function createBooking(userId: number, bookingDate: string, bookingTime: string) {
  const { data, error } = await supabase.from('bookings')
    .insert({ user_id: userId, booking_date: bookingDate, booking_time: bookingTime, status: 'pending' })
    .select().single();
  if (error) throw error;
  return data;
}

export async function getUserBookings(userId: number) {
  const { data, error } = await supabase.from('bookings').select('*')
    .eq('user_id', userId).order('booking_date', { ascending: false }).order('booking_time', { ascending: false });
  if (error) throw error;
  return data;
}

export async function cancelBooking(bookingId: number) {
  const { error } = await supabase.from('bookings').update({ status: 'cancelled' }).eq('id', bookingId);
  if (error) throw error;
}

/* ═══════════════════════════════════════════════════════
   GAME ROOM
   ═══════════════════════════════════════════════════════ */

export async function getUserPurchases(userId: number): Promise<string[]> {
  const { data, error } = await supabase
    .from('user_purchases').select('item_id').eq('user_id', userId);
  if (error) throw error;
  return (data || []).map((r: any) => r.item_id);
}

export async function purchaseItem(userId: number, itemId: string, price: number): Promise<void> {
  // Check balance first
  const { data: pr } = await supabase.from('player_ratings').select('coins_balance').eq('user_id', userId).single();
  if (!pr) throw new Error('Player rating not found');
  if ((pr.coins_balance || 0) < price) throw new Error('Недостаточно монет');

  const { error } = await supabase
    .from('user_purchases').insert({ user_id: userId, item_id: itemId, price });
  if (error && error.code !== '23505') throw error;
  if (error?.code === '23505') return; // already purchased

  const { error: updateError } = await supabase.from('player_ratings')
    .update({ coins_balance: (pr.coins_balance || 0) - price })
    .eq('user_id', userId);
  if (updateError) throw updateError;
}

export async function updateEquippedItems(
  userId: number,
  updates: { equipped_bg?: string | null; equipped_char?: string | null }
): Promise<void> {
  const { error } = await supabase
    .from('player_ratings').update(updates).eq('user_id', userId);
  if (error) throw error;
}

/* ═══════════════════════════════════════════════════════
   ANALYTICS
   ═══════════════════════════════════════════════════════ */

export interface AnalyticsData {
  totalRevenue: number;       // cash collected (excludes HUcoin coverage)
  totalHucoinRevenue: number; // value collected in HUcoin, in ₽-equivalent
  totalOrders: number;
  paidOrders: number;
  avgCheck: number;
  totalTournaments: number;
  finishedTournaments: number;
  totalRegistrations: number;
  totalCheckIns: number;
  checkInRate: number;
  totalUsers: number;
  activeUsers: number; // users with at least 1 tournament
  newUsersThisPeriod: number;
  tournamentReports: TournamentReport[];
}

export interface TournamentReport {
  id: number;
  name: string;
  date: string;
  type: string;
  registrations: number;
  checkIns: number;
  checkInRate: number;
  revenue: number;          // cash collected (excludes HUcoin coverage)
  hucoinRevenue: number;    // value collected in HUcoin, in ₽-equivalent
  totalOrderAmount: number;
  players: number;
  stacks: number;
  reentries: number;
  stacksAmount: number;
  reentriesAmount: number;
  foodOrdersCount: number;
  foodOrdersTotal: number;
  foodOrdersPaid: number;   // cash
  foodOrdersHucoin: number; // HUcoin (₽-equivalent)
}

export interface TournamentItemStat {
  name: string;
  count: number;
  total: number;
}

export interface TournamentDetail {
  tournamentId: number;
  stacks: number;
  reentries: number;
  stacksAmount: number;
  reentriesAmount: number;
  foodOrdersCount: number;
  foodOrdersTotal: number;
  foodOrdersPaid: number;   // cash
  foodOrdersHucoin: number; // HUcoin (₽-equivalent)
  totalAmount: number;
  totalPaid: number;        // cash collected
  hucoinPaid: number;       // collected in HUcoin, ₽-equivalent
  topItems: TournamentItemStat[];
}

// Fetch all rows from a table by paginating through .range() until done.
// Supabase caps each request at ~1000 rows; for analytics we need everything.
async function fetchAllPaginated<T = any>(
  build: () => any,
  pageSize = 1000,
): Promise<T[]> {
  const all: T[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await build().range(from, from + pageSize - 1);
    if (error) throw error;
    const rows = (data as T[]) || [];
    all.push(...rows);
    if (rows.length < pageSize) break;
  }
  return all;
}

export async function getAnalytics(periodDays: number = 0): Promise<AnalyticsData> {
  const since = periodDays > 0
    ? new Date(Date.now() - periodDays * 86400000).toISOString()
    : '2020-01-01';

  // Parallel fetch — orders/registrations need pagination because there are
  // typically more than the 1000-row Supabase response cap.
  const [
    allOrders,
    { data: tournaments },
    { data: users },
    { data: ratings },
  ] = await Promise.all([
    fetchAllPaginated<any>(() => supabase.from('orders').select('*').gte('created_at', since).order('id', { ascending: true })),
    supabase.from('tournaments').select('*').gte('start_date', since).order('start_date', { ascending: false }),
    supabase.from('users').select('id, created_at, is_guest'),
    supabase.from('player_ratings').select('user_id, total_tournaments'),
  ]);

  const allTournaments = tournaments || [];
  const allUsers = (users || []).filter(u => !u.is_guest);

  // Fetch registrations for the tournaments in the period (paginated for safety)
  const tournamentIds = allTournaments.map(t => t.id);
  const allRegs = tournamentIds.length > 0
    ? await fetchAllPaginated<any>(() => supabase.from('tournament_registrations').select('*').in('tournament_id', tournamentIds).order('id', { ascending: true }))
    : [];
  const allRatings = ratings || [];

  // Revenue — cash and HUcoin kept separate so the two never mix in reports.
  const nonCancelled = allOrders.filter(o => o.status !== 'cancelled');
  const paidOrders = allOrders.filter(o => o.paid && o.status !== 'cancelled');
  const totalRevenue = nonCancelled.reduce((s, o) => s + orderCashRub(o), 0);
  const totalHucoinRevenue = nonCancelled.reduce((s, o) => s + orderCoinRub(o), 0);
  const avgCheck = paidOrders.length > 0 ? totalRevenue / paidOrders.length : 0;

  // Registrations & check-ins
  const registered = allRegs.filter(r => r.status === 'registered');
  const checkedIn = registered.filter(r => r.checked_in);
  const checkInRate = registered.length > 0 ? (checkedIn.length / registered.length) * 100 : 0;

  // Users
  const newUsersThisPeriod = allUsers.filter(u => u.created_at >= since).length;
  const activeUsers = allRatings.filter(r => (r.total_tournaments || 0) > 0).length;

  // Tournament reports
  const tournamentReports: TournamentReport[] = allTournaments.map(t => {
    const tRegs = allRegs.filter(r => r.tournament_id === t.id && r.status === 'registered');
    const tCheckIns = tRegs.filter(r => r.checked_in);
    const tAllOrders = allOrders.filter(o => o.tournament_id === t.id && o.status !== 'cancelled');
    const tRevenue = tAllOrders.reduce((s, o) => s + orderCashRub(o), 0);
    const tHucoinRevenue = tAllOrders.reduce((s, o) => s + orderCoinRub(o), 0);
    const tTotalAmount = tAllOrders.reduce((s, o) => s + (o.total_price || 0), 0);

    let stacks = 0, reentries = 0, stacksAmount = 0, reentriesAmount = 0;
    let foodOrdersCount = 0, foodOrdersTotal = 0, foodOrdersPaid = 0, foodOrdersHucoin = 0;
    for (const o of tAllOrders) {
      const items: any[] = typeof o.items === 'string' ? (() => { try { return JSON.parse(o.items); } catch { return []; } })() : (o.items || []);
      const isStack = items.some((it: any) => (it.name || '').includes('Стартовый стек'));
      const isReentry = items.some((it: any) => (it.name || '').includes('Ре-ентри'));
      if (isStack) { stacks++; stacksAmount += o.total_price || 0; }
      else if (isReentry) { reentries++; reentriesAmount += o.total_price || 0; }
      else { foodOrdersCount++; foodOrdersTotal += o.total_price || 0; foodOrdersPaid += orderCashRub(o); foodOrdersHucoin += orderCoinRub(o); }
    }

    return {
      id: t.id,
      name: t.name,
      date: t.start_date,
      type: t.tournament_type,
      registrations: tRegs.length,
      checkIns: tCheckIns.length,
      checkInRate: tRegs.length > 0 ? (tCheckIns.length / tRegs.length) * 100 : 0,
      revenue: tRevenue,
      hucoinRevenue: tHucoinRevenue,
      totalOrderAmount: tTotalAmount,
      players: t.current_players || 0,
      stacks, reentries, stacksAmount, reentriesAmount,
      foodOrdersCount, foodOrdersTotal, foodOrdersPaid, foodOrdersHucoin,
    };
  });

  return {
    totalRevenue,
    totalHucoinRevenue,
    totalOrders: allOrders.length,
    paidOrders: paidOrders.length,
    avgCheck: Math.round(avgCheck),
    totalTournaments: allTournaments.length,
    finishedTournaments: allTournaments.filter(t => t.is_finished).length,
    totalRegistrations: registered.length,
    totalCheckIns: checkedIn.length,
    checkInRate: Math.round(checkInRate),
    totalUsers: allUsers.length,
    activeUsers,
    newUsersThisPeriod,
    tournamentReports,
  };
}

export async function getTournamentAnalytics(tournamentId: number): Promise<TournamentDetail> {
  const allOrders = await fetchAllPaginated<any>(() =>
    supabase.from('orders').select('*').eq('tournament_id', tournamentId).neq('status', 'cancelled').order('id', { ascending: true })
  );

  // Orders created from the menu store only menu_item_id+quantity in items[]; name and
  // price aren't baked in. Look up current name/price from menu_items so the popular-
  // positions table shows real names and non-zero amounts.
  const { data: menuItemsRaw } = await supabase.from('menu_items').select('id, name, price');
  const menuMap: Record<number, { name: string; price: number }> = {};
  for (const mi of menuItemsRaw || []) menuMap[mi.id] = { name: mi.name, price: mi.price || 0 };

  let stacks = 0, reentries = 0, stacksAmount = 0, reentriesAmount = 0;
  let foodOrdersCount = 0, foodOrdersTotal = 0, foodOrdersPaid = 0, foodOrdersHucoin = 0;
  const itemCounts: Record<string, { count: number; total: number }> = {};

  for (const o of allOrders) {
    const items: any[] = typeof o.items === 'string' ? (() => { try { return JSON.parse(o.items); } catch { return []; } })() : (o.items || []);
    const isStack = items.some((it: any) => (it.name || '').includes('Стартовый стек'));
    const isReentry = items.some((it: any) => (it.name || '').includes('Ре-ентри'));
    if (isStack) { stacks++; stacksAmount += o.total_price || 0; }
    else if (isReentry) { reentries++; reentriesAmount += o.total_price || 0; }
    else {
      foodOrdersCount++;
      foodOrdersTotal += o.total_price || 0;
      foodOrdersPaid += orderCashRub(o);
      foodOrdersHucoin += orderCoinRub(o);
      for (const item of items) {
        const lookup = item.menu_item_id != null ? menuMap[item.menu_item_id] : undefined;
        const name = item.name || lookup?.name || `#${item.menu_item_id}`;
        const unitPrice = item.price ?? lookup?.price ?? 0;
        if (!itemCounts[name]) itemCounts[name] = { count: 0, total: 0 };
        itemCounts[name].count += item.quantity || 1;
        itemCounts[name].total += unitPrice * (item.quantity || 1);
      }
    }
  }

  const topItems = Object.entries(itemCounts)
    .map(([name, s]) => ({ name, ...s }))
    .sort((a, b) => b.count - a.count);

  const totalAmount = stacksAmount + reentriesAmount + foodOrdersTotal;
  const totalPaid = allOrders.reduce((s, o) => s + orderCashRub(o), 0);
  const hucoinPaid = allOrders.reduce((s, o) => s + orderCoinRub(o), 0);

  return { tournamentId, stacks, reentries, stacksAmount, reentriesAmount, foodOrdersCount, foodOrdersTotal, foodOrdersPaid, foodOrdersHucoin, totalAmount, totalPaid, hucoinPaid, topItems };
}

export function exportAnalyticsCSV(reports: TournamentReport[]): string {
  const header = 'Турнир;Дата;Тип;Записи;Check-in;Конверсия %;Выручка кэш ₽;HUcoin ₽;Игроков';
  const rows = reports.map(r =>
    `${r.name};${new Date(r.date).toLocaleDateString('ru-RU')};${r.type};${r.registrations};${r.checkIns};${Math.round(r.checkInRate)};${r.revenue};${r.hucoinRevenue};${r.players}`
  );
  return [header, ...rows].join('\n');
}
